import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit, Trash2, Upload, Search, Filter, FileSpreadsheet, Download, AlertCircle, CheckCircle2, RefreshCw, TrendingUp, MoreVertical, Eye, Camera, Image, FileText, Calendar, Loader, Loader2, X, BarChart3, ChevronDown, ChevronRight, CreditCard, MessageCircle } from 'lucide-react';
import { formatDateDisplay } from '../utils/dateUtils';
import { downloadExcel, downloadPDF } from '../utils/downloadHelpers';
import { toast } from 'sonner';
import FileUpload from '../components/FileUpload';
import { useTranslation } from 'react-i18next';
import { SortButton, sortData, DateRangeFilter, SearchFilters, filterData } from '../components/DataTableWithFilters';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import DraftFinesTab from '../components/DraftFinesTab';
import { usePermissions } from '../utils/permissionUtils';
import { CurrencyDisplay } from '../components/DirhamSymbol';
import useDeviceDetection from '../hooks/useDeviceDetection';
import Pagination from '../components/Pagination';
import { formatErrorMessage } from '../utils/formatError';
import PlatePair from '../components/PlatePair';
import AddFineDiscountDialog from '../components/AddFineDiscountDialog';

const Fines = ({ user }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile, isTablet } = useDeviceDetection();
  
  // Permission checks for module
  const permissions = usePermissions(user, 'fines');
  const canCreate = permissions.canCreate;
  const canEdit = permissions.canEdit;
  const canDelete = permissions.canDelete;
  const canManage = canCreate || canEdit || canDelete;
  
  const [fines, setFines] = useState([]);
  const [reverseFineDialog, setReverseFineDialog] = useState({ open: false, fine: null });
  const [reverseReason, setReverseReason] = useState('');
  const [reverseCorrectPlate, setReverseCorrectPlate] = useState('');
  const [reverseProofFile, setReverseProofFile] = useState(null);
  const [reverseSubmitting, setReverseSubmitting] = useState(false);
  const [motorcycles, setMotorcycles] = useState([]);
  const [riders, setRiders] = useState([]);
  const [lessees, setLessees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedFine, setSelectedFine] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [draftFinesCount, setDraftFinesCount] = useState(0);
  const [activeTab, setActiveTab] = useState('fines');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 50;
  
  // Server-side search
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Stats for summary cards (fetched separately, not affected by pagination)
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    appealed: 0,
    total_amount: 0,
    paid_amount: 0,
    unpaid_amount: 0
  });
  
  // Import Fine Report dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('skip');
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  
  // Ticket photo upload state
  const [ticketPhotoDialogOpen, setTicketPhotoDialogOpen] = useState(false);
  const [ticketPhotoFine, setTicketPhotoFine] = useState(null);
  const [ticketPhotoFile, setTicketPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Bulk ticket-photo upload — one folder-select, matches filename ↔ ticket_no
  const bulkTicketPhotoInputRef = useRef(null);
  const [bulkPhotoUploading, setBulkPhotoUploading] = useState(false);
  const [bulkPhotoResult, setBulkPhotoResult] = useState(null);
  const [bulkPhotoDialogOpen, setBulkPhotoDialogOpen] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  
  // Update Fine dialog state
  const [updateFineDialogOpen, setUpdateFineDialogOpen] = useState(false);
  const [updateFine, setUpdateFine] = useState(null);
  const [updateStatus, setUpdateStatus] = useState('');
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [updatingFine, setUpdatingFine] = useState(false);

  // Edit Fine (core details) — Admin / Super Admin only
  const [editFine, setEditFine] = useState(null);
  const [editForm, setEditForm] = useState({ fine_date: '', fine_time: '', amount: '', fine_number: '', payment_ref_no: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  // Documents viewer modal for Ticket Photo + Zoho Invoice.
  const [docsViewFine, setDocsViewFine] = useState(null);
  // Add / Replace Documents dialog (open to anyone with edit
  // permissions — not restricted to Admin / Super Admin).
  const [docsUploadFine, setDocsUploadFine] = useState(null);
  const [docsTicketPhoto, setDocsTicketPhoto] = useState(null);
  const [docsZohoInvoice, setDocsZohoInvoice] = useState(null);
  const [docsZohoInvoiceNo, setDocsZohoInvoiceNo] = useState('');
  const [docsZohoInvoiceDate, setDocsZohoInvoiceDate] = useState('');
  const [savingDocs, setSavingDocs] = useState(false);

  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'desc' });
  
  // Bulk selection state
  const [selectedFines, setSelectedFines] = useState([]);
  
  // Summary filter state (for interactive cards)
  const [summaryFilter, setSummaryFilter] = useState(null); // 'paid', 'unpaid', or null
  const [showBikeRankingDialog, setShowBikeRankingDialog] = useState(false);
  
  // Admin fee config state
  const [adminFeeConfig, setAdminFeeConfig] = useState(null);

  // Lessee Monthly Summary state
  const [lesseeSummaryExpanded, setLesseeSummaryExpanded] = useState(false);
  const [lesseeSummary, setLesseeSummary] = useState([]);
  const [lesseeSummaryLoading, setLesseeSummaryLoading] = useState(false);
  const [expandedSummaryRow, setExpandedSummaryRow] = useState(null);
  const [expandedRowFines, setExpandedRowFines] = useState(null);

  // Rider Pay (Direct pay by Rider) Monthly Summary state
  const [riderPaySummaryExpanded, setRiderPaySummaryExpanded] = useState(false);
  const [riderPaySummary, setRiderPaySummary] = useState([]);
  const [riderPaySummaryTotals, setRiderPaySummaryTotals] = useState(null);
  const [riderPaySummaryLoading, setRiderPaySummaryLoading] = useState(false);

  // Add Fine Discount dialog state
  const [discountFine, setDiscountFine] = useState(null);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);

  // Monthly Audit panel state
  const [auditExpanded, setAuditExpanded] = useState(false);
  const [auditData, setAuditData] = useState({ rows: [], totals: {} });
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditGroupBy, setAuditGroupBy] = useState('month'); // month | lessee | rider
  const [auditYear, setAuditYear] = useState('');
  const [auditMonth, setAuditMonth] = useState('');

  // Mark Paid With Proof dialog state (centralised — used by single / bulk / lessee-month / audit row)
  const [paidProofDialog, setPaidProofDialog] = useState({
    open: false,
    mode: 'single', // 'single' | 'bulk' | 'lessee_month'
    fineIds: [],
    lesseeName: '',
    month: '',
    contextLabel: '', // human-readable description in dialog
    fineCount: 0,
    existingProofUrl: '',  // when editing an already-paid fine (single mode)
  });
  const [paidProofForm, setPaidProofForm] = useState({
    payment_source: 'rider_direct',
    payment_ref_no: '',
    payment_date: '',
    payment_time: '',
    notes: '',
    replace_reason: '',
    file: null,
  });
  const [paidProofSubmitting, setPaidProofSubmitting] = useState(false);

  // Proof history viewer state
  const [proofHistoryDialog, setProofHistoryDialog] = useState({ open: false, fineId: '', data: null, loading: false });

  // Authority payment dialog state (edit RTA/Municipality payment status).
  const [authDialog, setAuthDialog] = useState({
    open: false, fine: null, status: 'Not Paid', payment_date: '', receipt: null, saving: false,
  });
  // Authority payment RECEIPT viewer (read-only preview + download).
  const [authReceiptViewer, setAuthReceiptViewer] = useState({ open: false, url: '', fineNumber: '' });

  // Filter state
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    lessee: '',
    rider: '',
    bike: ''
  });
  
  const [formData, setFormData] = useState({
    fine_number: '',
    fine_date: '',
    fine_time: '',
    bike_plate_number: '',
    bike_golden_plate_number: '',
    bike_id: '',
    rider_id: '',
    rider_name: '',
    lessee_name: '',
    amount: 0,
    admin_fees: 0,
    reason: '',
    source: '',
    city: 'Dubai',
    status: 'pending',
    ticket_photo_url: ''
  });

  useEffect(() => {
    fetchData();
    fetchDraftFinesCount();
    fetchAdminFeeConfig();
  }, []);
  
  const fetchAdminFeeConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/fee-config`, {
        headers: getAuthHeader()
      });
      setAdminFeeConfig(response.data);
    } catch (error) {
      console.error('Error fetching admin fee config:', error);
    }
  };

  const fetchDraftFinesCount = async () => {
    try {
      const response = await axios.get(`${API}/draft-fines`, {
        headers: getAuthHeader()
      });
      setDraftFinesCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching draft fines count:', error);
    }
  };

  // Apply filters and sorting
  const normalizedFines = fines.map(fine => ({
    ...fine,
    date: fine.fine_date || fine.date_of_fine
  }));
  
  // Summary filter is now handled server-side
  const filteredByStatus = normalizedFines;
  
  const filteredFines = filterData(filteredByStatus, filters);
  const sortedFines = sortData(filteredFines, sortConfig.field, sortConfig.direction);
  
  // Calculate summary statistics
  // Summary stats now comes from backend stats API, not calculated from paginated data
  const summaryStats = {
    totalPaid: stats.paid_amount || 0,
    totalUnpaid: stats.unpaid_amount || 0,
    paidCount: stats.paid || 0,
    unpaidCount: (stats.pending || 0) + (stats.appealed || 0),
    totalAmount: stats.total_amount || 0,
    riderPayCount: stats.rider_pay_count || 0,
    riderPayTotal: stats.rider_pay_amount || 0,
  };
  
  // Calculate bike with highest fine (from current page only - for now)
  const bikeFineTotals = {};
  fines.forEach(fine => {
    const plate = fine.bike_plate_number || fine.plate_number || fine.motorcycle_plate || 'Unknown';
    const total = (fine.amount || fine.fine_amount || 0) + (fine.admin_fees || fine.admin_fee || 0);
    if (!bikeFineTotals[plate]) {
      bikeFineTotals[plate] = { plate, count: 0, total: 0 };
    }
    bikeFineTotals[plate].count += 1;
    bikeFineTotals[plate].total += total;
  });
  const sortedBikesByFines = Object.values(bikeFineTotals).sort((a, b) => b.total - a.total);
  const highestFineBike = sortedBikesByFines[0] || null;
  
  // Bulk selection functions
  const toggleFineSelection = (fineId) => {
    setSelectedFines(prev =>
      prev.includes(fineId)
        ? prev.filter(id => id !== fineId)
        : [...prev, fineId]
    );
  };
  
  const toggleSelectAll = () => {
    if (selectedFines.length === sortedFines.length) {
      setSelectedFines([]);
    } else {
      setSelectedFines(sortedFines.map(f => f.id));
    }
  };
  
  // Bulk status update
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedFines.length === 0) {
      toast.error('Please select at least one fine');
      return;
    }

    // "paid" requires proof of payment — open dialog
    if (newStatus === 'paid') {
      openPaidProofDialog({
        mode: 'bulk',
        fineIds: [...selectedFines],
        contextLabel: `${selectedFines.length} selected fine(s)`,
      });
      return;
    }

    try {
      await axios.patch(
        `${API}/fines/bulk-status`,
        { fine_ids: selectedFines, status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success(`${selectedFines.length} fine(s) updated to ${newStatus}`);
      setSelectedFines([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  // Single fine status update
  const handleStatusChange = async (fineId, newStatus) => {
    try {
      await axios.patch(
        `${API}/fines/${fineId}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  // Backfill names from motorcycles
  const handleBackfillNames = async () => {
    try {
      toast.info('Refreshing names... This may take a moment.');
      const response = await axios.post(
        `${API}/fines/backfill-names`,
        {},
        { headers: getAuthHeader() }
      );
      const data = response.data;
      
      if (data.updated_count > 0) {
        toast.success(`Updated ${data.updated_count} records!`);
      } else if (data.not_found_count > 0) {
        toast.warning(`No records updated. ${data.not_found_count} plates not found in motorcycles.`);
      } else {
        toast.info('No records needed updating.');
      }
      
      fetchData();
    } catch (error) {
      console.error('Backfill error:', error);
      toast.error(formatErrorMessage(error, 'Failed to refresh names'));
    }
  };
  
  // Calculate admin fee based on config
  const calculateAdminFee = (amount) => {
    if (!adminFeeConfig || !adminFeeConfig.fines) return 0;
    
    const fineConfig = adminFeeConfig.fines;
    if (fineConfig.fee_type === 'percentage') {
      return Math.round(amount * (fineConfig.percentage / 100) * 100) / 100;
    }
    return fineConfig.fixed_amount || 0;
  };

  const handleViewDetails = (fine) => {
    setSelectedFine(fine);
    setDetailDialogOpen(true);
  };

  const handleOpenUpdateFine = (fine) => {
    setUpdateFine(fine);
    setUpdateStatus(fine.status || 'unpaid');
    setPaymentProofFile(null);
    setUpdateFineDialogOpen(true);
  };

  // Edit-fine (Admin/Super Admin) — opens a small dialog to change the
  // core fine details: date, time, amount, ticket no., reference no.
  const handleOpenEditFine = (fine) => {
    setEditFine(fine);
    setEditForm({
      fine_date: fine.fine_date || fine.date || '',
      fine_time: fine.fine_time || fine.time || '',
      amount: String(fine.amount ?? ''),
      fine_number: fine.fine_number || fine.ticket_no || '',
      payment_ref_no: fine.payment_ref_no || fine.reference_no || '',
    });
  };

  const submitEditFine = async () => {
    if (!editFine) return;
    const payload = {
      fine_date: editForm.fine_date || null,
      fine_time: editForm.fine_time || null,
      amount: editForm.amount === '' ? null : Number(editForm.amount),
      fine_number: editForm.fine_number || null,
      payment_ref_no: editForm.payment_ref_no || null,
    };
    if (payload.amount !== null && (isNaN(payload.amount) || payload.amount < 0)) {
      toast.error('Amount must be a positive number'); return;
    }
    try {
      setSavingEdit(true);
      await axios.put(
        `${API}/fines/${editFine.id}`,
        payload,
        { headers: getAuthHeader() }
      );
      toast.success('Fine updated');
      setEditFine(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update fine');
    } finally {
      setSavingEdit(false);
    }
  };

  // Add / Replace Documents — open to anyone with fine-edit
  // permission. Uploads Ticket Photo and/or Zoho Invoice via the
  // existing `/upload-doc` endpoint. Either file is optional; the
  // user can also just add one.
  const handleOpenDocsUpload = (fine) => {
    setDocsUploadFine(fine);
    setDocsTicketPhoto(null);
    setDocsZohoInvoice(null);
    setDocsZohoInvoiceNo(fine.zoho_invoice_no || '');
    setDocsZohoInvoiceDate(fine.zoho_invoice_date || '');
  };

  const submitDocsUpload = async () => {
    if (!docsUploadFine) return;
    const invoiceNoChanged = (docsZohoInvoiceNo || '') !== (docsUploadFine.zoho_invoice_no || '');
    const invoiceDateChanged = (docsZohoInvoiceDate || '') !== (docsUploadFine.zoho_invoice_date || '');
    if (!docsTicketPhoto && !docsZohoInvoice && !invoiceNoChanged && !invoiceDateChanged) {
      toast.error('Please select a file or update the invoice details');
      return;
    }
    try {
      setSavingDocs(true);
      const uploads = [];
      if (docsTicketPhoto) {
        const fd = new FormData();
        fd.append('doc_type', 'ticket_photo');
        fd.append('file', docsTicketPhoto);
        uploads.push(axios.post(`${API}/fines/${docsUploadFine.id}/upload-doc`, fd, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
        }));
      }
      if (docsZohoInvoice) {
        const fd = new FormData();
        fd.append('doc_type', 'zoho_invoice');
        fd.append('file', docsZohoInvoice);
        uploads.push(axios.post(`${API}/fines/${docsUploadFine.id}/upload-doc`, fd, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
        }));
      }
      if (uploads.length) await Promise.all(uploads);
      // Persist Zoho invoice metadata (no. + date) if provided/changed.
      if (invoiceNoChanged || invoiceDateChanged) {
        await axios.put(
          `${API}/fines/${docsUploadFine.id}`,
          {
            zoho_invoice_no: docsZohoInvoiceNo || null,
            zoho_invoice_date: docsZohoInvoiceDate || null,
          },
          { headers: getAuthHeader() }
        );
      }
      toast.success('Documents saved');
      setDocsUploadFine(null);
      setDocsTicketPhoto(null);
      setDocsZohoInvoice(null);
      setDocsZohoInvoiceNo('');
      setDocsZohoInvoiceDate('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setSavingDocs(false);
    }
  };

  const handleSubmitUpdateFine = async () => {
    if (!updateFine) return;
    
    // If status is being changed to "paid", redirect to the strict
    // Mark-Paid-With-Proof dialog (proof + ref# + date + time mandatory).
    if (updateStatus === 'paid') {
      setUpdateFineDialogOpen(false);
      openPaidProofDialog({
        mode: 'single',
        fineIds: [updateFine.id],
        contextLabel: `Fine #${updateFine.fine_number || updateFine.ticket_number || updateFine.id.slice(0, 8)}`,
        existingProofUrl: updateFine.proof_file_url || updateFine.payment_proof_url || '',
      });
      return;
    }
    
    setUpdatingFine(true);
    try {
      await axios.put(`${API}/fines/${updateFine.id}`, {
        ...updateFine,
        status: updateStatus,
      }, { headers: getAuthHeader() });
      
      toast.success('Fine updated successfully');
      setUpdateFineDialogOpen(false);
      setUpdateFine(null);
      setPaymentProofFile(null);
      fetchData();
    } catch (error) {
      console.error('Error updating fine:', error);
      toast.error('Failed to update fine');
    } finally {
      setUpdatingFine(false);
    }
  };

  // ===== Mark Paid With Proof (centralised handler) =====
  const openPaidProofDialog = ({ mode, fineIds = [], lesseeName = '', month = '', contextLabel = '', existingProofUrl = '' }) => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const hh = String(today.getHours()).padStart(2, '0');
    const mi = String(today.getMinutes()).padStart(2, '0');
    setPaidProofDialog({
      open: true,
      mode,
      fineIds,
      lesseeName,
      month,
      contextLabel,
      fineCount: fineIds.length || 0,
      existingProofUrl,
    });
    setPaidProofForm({
      payment_source: mode === 'lessee_month' ? 'lessee_claim' : 'rider_direct',
      payment_ref_no: '',
      payment_date: `${yyyy}-${mm}-${dd}`,
      payment_time: `${hh}:${mi}`,
      notes: '',
      replace_reason: '',
      file: null,
    });
  };

  const closePaidProofDialog = () => {
    setPaidProofDialog({ open: false, mode: 'single', fineIds: [], lesseeName: '', month: '', contextLabel: '', fineCount: 0, existingProofUrl: '' });
    setPaidProofForm({ payment_source: 'rider_direct', payment_ref_no: '', payment_date: '', payment_time: '', notes: '', replace_reason: '', file: null });
  };

  const submitPaidProofDialog = async () => {
    const f = paidProofForm;
    const d = paidProofDialog;

    if (!f.payment_source) { toast.error('Select a payment source'); return; }
    if (!f.payment_ref_no || !f.payment_ref_no.trim()) { toast.error('Payment reference number is required'); return; }
    if (!f.payment_date) { toast.error('Payment date is required'); return; }
    if (!f.payment_time) { toast.error('Payment time is required'); return; }

    const replacing = d.mode === 'single' && d.existingProofUrl;
    if (replacing) {
      // Editing/replacing existing proof requires a descriptive reason.
      if (!f.replace_reason || f.replace_reason.trim().length < 5) {
        toast.error('Please describe the reason for replacing the existing proof (min 5 chars)');
        return;
      }
      if (!f.file) { toast.error('Select a new proof file'); return; }
    } else if (!f.file) {
      toast.error('Proof of payment file is required');
      return;
    }

    // Validate file
    if (f.file) {
      if (f.file.size > 10 * 1024 * 1024) { toast.error('File exceeds 10 MB limit'); return; }
      const okType = f.file.type.startsWith('image/') || f.file.type === 'application/pdf';
      if (!okType) { toast.error('Only image or PDF files are allowed'); return; }
    }

    setPaidProofSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('proof_file', f.file);
      formData.append('payment_source', f.payment_source);
      formData.append('payment_ref_no', f.payment_ref_no.trim());
      formData.append('payment_date', f.payment_date);
      formData.append('payment_time', f.payment_time);
      if (f.notes) formData.append('notes', f.notes);

      let url;
      if (replacing) {
        url = `${API}/fines/${d.fineIds[0]}/replace-proof`;
        formData.append('replace_reason', f.replace_reason.trim());
      } else if (d.mode === 'lessee_month') {
        url = `${API}/fines/mark-paid-with-proof`;
        formData.append('lessee_name', d.lesseeName);
        formData.append('month', d.month);
      } else {
        url = `${API}/fines/mark-paid-with-proof`;
        formData.append('fine_ids', d.fineIds.join(','));
      }

      const res = await axios.post(url, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message || 'Proof recorded successfully');
      closePaidProofDialog();
      setSelectedFines([]);
      if (lesseeSummaryExpanded) fetchLesseeSummary();
      if (auditExpanded) fetchMonthlyAudit();
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to mark as paid';
      toast.error(typeof msg === 'string' ? msg : 'Failed to mark as paid');
    } finally {
      setPaidProofSubmitting(false);
    }
  };

  const openProofHistory = async (fineId) => {
    setProofHistoryDialog({ open: true, fineId, data: null, loading: true });
    try {
      const res = await axios.get(`${API}/fines/${fineId}/payment-history`, { headers: getAuthHeader() });
      setProofHistoryDialog({ open: true, fineId, data: res.data, loading: false });
    } catch (e) {
      toast.error('Failed to load proof history');
      setProofHistoryDialog({ open: false, fineId: '', data: null, loading: false });
    }
  };
  // ===== End Mark Paid With Proof =====

  const fetchData = async (page = currentPage, search = activeSearch, paymentStatus = null) => {
    try {
      setLoading(true);
      if (search) setIsSearching(true);
      const skip = (page - 1) * ITEMS_PER_PAGE;
      
      const params = new URLSearchParams();
      params.append('limit', ITEMS_PER_PAGE);
      params.append('skip', skip);
      if (search) params.append('search', search);
      if (paymentStatus && paymentStatus !== 'all') {
        params.append('status', paymentStatus);
      }
      
      // Build count params (same filters)
      const countParams = new URLSearchParams();
      if (search) countParams.append('search', search);
      if (paymentStatus && paymentStatus !== 'all') {
        countParams.append('status', paymentStatus);
      }
      
      const [finesRes, countRes, statsRes, bikesRes, ridersRes, lesseesRes] = await Promise.all([
        axios.get(`${API}/fines?${params.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/fines/count?${countParams.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/fines/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/lessees`, { headers: getAuthHeader() })
      ]);
      setFines(finesRes.data);
      setTotalCount(countRes.data.count || 0);
      setStats(statsRes.data);
      setMotorcycles(bikesRes.data);
      setRiders(ridersRes.data);
      setLessees(lesseesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };
  
  // Trigger search manually (button click or Enter key)
  const handleSearch = () => {
    setActiveSearch(searchQuery);
    setCurrentPage(1);
    fetchData(1, searchQuery);
  };

  // When search text is cleared (backspace/delete), auto-reset to original state
  useEffect(() => {
    if (searchQuery === '' && activeSearch !== '') {
      setActiveSearch('');
      setCurrentPage(1);
      fetchData(1, '');
    }
  }, [searchQuery]);

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setCurrentPage(1);
    fetchData(1, '');
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, activeSearch, summaryFilter);
  };

  const handleBikeSelect = (bikeId) => {
    const bike = motorcycles.find(b => b.id === bikeId);
    if (bike) {
      let bikeData = {
        bike_id: bikeId,
        bike_plate_number: bike.plate_number,
        bike_golden_plate_number: bike.golden_plate_number || '',
        lessee_name: bike.lessee_name || ''
      };
      
      if (bike.assigned_rider_id || bike.rider_id) {
        const assignedRiderId = bike.assigned_rider_id || bike.rider_id;
        const assignedRider = riders.find(r => r.id === assignedRiderId);
        
        if (assignedRider) {
          bikeData.rider_id = assignedRider.id;
          bikeData.rider_name = assignedRider.name;
          toast.success(`Auto-filled rider: ${assignedRider.name}`);
        }
      }
      
      setFormData({
        ...formData,
        ...bikeData
      });
    }
  };

  const handleRiderSelect = (riderId) => {
    const rider = riders.find(r => r.id === riderId);
    if (rider) {
      setFormData({
        ...formData,
        rider_id: riderId,
        rider_name: rider.name
      });
    }
  };
  
  // Auto-calculate admin fee when amount changes
  const handleAmountChange = (newAmount) => {
    const amount = parseFloat(newAmount) || 0;
    const adminFee = calculateAdminFee(amount);
    setFormData({
      ...formData,
      amount: amount,
      admin_fees: adminFee
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/fines/${editingId}`, formData, { headers: getAuthHeader() });
        toast.success('Fine updated successfully');
      } else {
        await axios.post(`${API}/fines`, formData, { headers: getAuthHeader() });
        toast.success('Fine added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (fine) => {
    setEditingId(fine.id);
    setFormData({
      fine_number: fine.fine_number || '',
      fine_date: fine.fine_date || fine.date_of_fine,
      fine_time: fine.fine_time || '',
      bike_plate_number: fine.bike_plate_number || fine.motorcycle_plate,
      bike_golden_plate_number: fine.bike_golden_plate_number || '',
      bike_id: fine.bike_id,
      rider_id: fine.rider_id,
      rider_name: fine.rider_name,
      lessee_name: fine.lessee_name || '',
      amount: fine.amount || fine.fine_amount || 0,
      admin_fees: fine.admin_fees || fine.admin_fee || 0,
      reason: fine.reason || fine.fine_type || '',
      source: fine.source || '',
      city: fine.city || 'Dubai',
      status: fine.status,
      ticket_photo_url: fine.ticket_photo_url || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this fine?')) {
      try {
        await axios.delete(`${API}/fines/${id}`, { headers: getAuthHeader() });
        toast.success('Fine deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete fine');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      fine_number: '',
      fine_date: '',
      fine_time: '',
      bike_plate_number: '',
      bike_golden_plate_number: '',
      bike_id: '',
      rider_id: '',
      rider_name: '',
      lessee_name: '',
      amount: 0,
      admin_fees: 0,
      reason: '',
      source: '',
      city: 'Dubai',
      status: 'pending',
      ticket_photo_url: ''
    });
    setEditingId(null);
  };

  // Upload Ticket Photo function
  const handleUploadTicketPhoto = async () => {
    if (!ticketPhotoFile || !ticketPhotoFine) return;
    
    setUploadingPhoto(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        // Update fine with ticket photo
        await axios.put(`${API}/fines/${ticketPhotoFine.id}`, {
          ...ticketPhotoFine,
          ticket_photo_url: base64Data
        }, { headers: getAuthHeader() });
        
        toast.success('Ticket photo uploaded successfully');
        setTicketPhotoDialogOpen(false);
        setTicketPhotoFile(null);
        setTicketPhotoFine(null);
        fetchData();
      };
      reader.readAsDataURL(ticketPhotoFile);
    } catch (error) {
      console.error('Error uploading ticket photo:', error);
      toast.error('Failed to upload ticket photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Backfill WhatsApp for fines imported in the last 72 hours that never
  // got the `fine_notification` template sent (e.g. rows created before the
  // auto-trigger was wired to the bulk import path).
  const handleBackfillWhatsApp = async () => {
    if (!window.confirm('Send fine_notification WhatsApp for fines imported in the last 72 hours that have no notification yet?\n\nOnly fines whose rider is opted-in and template is approved will actually deliver.')) return;
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/fines/backfill-whatsapp?hours=72`, {}, { headers: getAuthHeader() });
      const d = res.data;
      toast.success(`Scanned ${d.scanned} fines · fired ${d.fired} WhatsApp · ${d.already_notified} already sent · ${d.no_recipient} skipped (no recipient / opt-out)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };


  // Bulk-upload ticket photos — filename (without extension) must equal
  // the fine's ticket_no. Multi-select files (any browser) OR select an
  // entire folder (Chrome/Edge/Safari with `webkitdirectory`).
  const handleBulkTicketPhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBulkPhotoUploading(true);
    setBulkPhotoResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f, f.name));
      const res = await axios.post(`${API}/fines/bulk-upload-ticket-photos`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setBulkPhotoResult(res.data);
      setBulkPhotoDialogOpen(true);
      const msg = `Uploaded ${res.data.matched_count} of ${files.length} — ${res.data.unmatched_count} unmatched, ${res.data.skipped_existing_count} already had photos`;
      if (res.data.matched_count > 0) {
        toast.success(msg);
      } else {
        toast.warning(msg);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk ticket-photo upload failed');
    } finally {
      setBulkPhotoUploading(false);
      // Reset the input so the same folder can be reselected if needed
      if (bulkTicketPhotoInputRef.current) bulkTicketPhotoInputRef.current.value = '';
    }
  };

  // Download ticket photo helper
  const downloadTicketPhoto = (dataUrl, fineName) => {
    if (!dataUrl) return;
    
    try {
      const [header, base64Data] = dataUrl.split(',');
      const mimeMatch = header.match(/data:([^;]+)/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
      
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ext = mimeType.includes('png') ? '.png' : '.jpg';
      link.download = `ticket_${fineName}${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Photo downloaded');
    } catch (error) {
      toast.error('Failed to download photo');
    }
  };

  // ============================================================
  // Zoho Books Import — column definitions and row builder.
  // Zoho expects ONE row per line-item; a "fine invoice" typically has
  // two rows sharing the same Invoice Number (the fine itself + the
  // admin fee), plus an optional third row for a discount as a
  // negative-price line. Zoho computes VAT itself, so we never emit
  // VAT as a separate line — only the admin fee row carries a "5%"
  // Item Tax so Zoho applies output VAT on it.
  // ============================================================
  const EMIRATE_TO_POS = {
    dubai: 'DU', dxb: 'DU', du: 'DU',
    sharjah: 'SHJ', shj: 'SHJ',
    'abu dhabi': 'AD', ad: 'AD', abudhabi: 'AD',
    'ras al khaimah': 'RAK', rak: 'RAK',
    ajman: 'AJ', aj: 'AJ',
    fujairah: 'FUJ', fuj: 'FUJ',
  };
  const ZOHO_COLUMNS = [
    'Invoice Date','Invoice Number','Estimate Number','Invoice Status','Customer Name',
    'VAT Treatment','Place Of Supply','Tax Registration Number','Is Inclusive Tax',
    'Invoice Level Tax','Invoice Level Tax %','Invoice Level Tax Type','Invoice Level Tax Exemption Reason',
    'Due Date','Expected Payment Date','PurchaseOrder','Template Name','Currency Code','Exchange Rate',
    'Discount Type','Is Discount Before Tax','Entity Discount Percent','Entity Discount Amount','Account',
    'Item Name','SKU','Item Desc','Quantity','Usage unit','Item Price','Discount','Discount Amount',
    'Expense Reference ID','Item Tax','Item Tax %','Item Tax Type','Item Type','Out of Scope Reason',
    'Item Tax Exemption Reason','Adjustment','Adjustment Description','Sales person',
    'Payment Terms','Payment Terms Label','Shipping Charge','Sales Order Number','Notes',
    'E-Commerce Operator Name','Terms & Conditions','CF.Invoice Type','CF.Fine Reference Number',
    'CF.Bike Plate Number','CF.Fine Source','CF.Rider Name','CF.Fine Description',
  ].map(h => ({ header: h, key: h, width: 18 }));

  const buildZohoRows = (fines, lesseeMap, lesseeIdMap = {}) => {
    const rows = [];
    fines.forEach((fine, idx) => {
      const invNo = idx + 1;
      // Normalise the raw fine_date (which may arrive as `DD-MM-YYYY`,
      // `DD/MM/YYYY`, or `YYYY-MM-DD…`) into a canonical YYYY-MM-DD
      // string. Everything downstream (Due Date + Invoice Date output)
      // uses this normalised value so Zoho gets ISO dates.
      const rawDate = (fine.fine_date || fine.date || fine.issued_date || fine.date_of_fine || '').trim();
      const invDate = (() => {
        if (!rawDate) return '';
        // Already YYYY-MM-DD or ISO-like
        if (/^\d{4}-\d{2}-\d{2}/.test(rawDate)) return rawDate.slice(0, 10);
        // DD-MM-YYYY or DD/MM/YYYY
        const m = rawDate.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
        if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
        // Last resort — let JS parse it
        const d = new Date(rawDate);
        return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
      })();
      const dueDate = (() => {
        if (!invDate) return '';
        // `invDate` is guaranteed YYYY-MM-DD at this point.
        const d = new Date(`${invDate}T00:00:00Z`);
        if (Number.isNaN(d.getTime())) return '';
        d.setUTCDate(d.getUTCDate() + 10);
        return d.toISOString().slice(0, 10);
      })();
      // Resolve lessee by id first (canonical), then by name (any alias
      // registered above). Both maps are built by the caller.
      const lesseeByName = lesseeMap[(fine.lessee_name || '').trim().toLowerCase()];
      const lesseeById = (
        lesseeIdMap[fine.lessee_id]
        || lesseeIdMap[fine.lesseeId]
        || lesseeIdMap[fine.lessee?.id]
      );
      const lessee = lesseeById || lesseeByName || {};
      // Lessee TRN lives on the DB as `trn_no` (canonical) or
      // `trn_number` (legacy). The older lookup fell back to `trn` /
      // `tax_registration_number` which never resolved, so every
      // Zoho export shipped with `non_vat_registered` and a blank
      // Tax Registration Number column. Preserve the two legacy
      // aliases so any older lessee document also resolves.
      const trn = (
        lessee.trn_no
        || lessee.trn_number
        || lessee.trn
        || lessee.tax_registration_number
        || fine.lessee_trn
        || ''
      );
      const vatTreatment = trn ? 'vat_registered' : 'non_vat_registered';
      // Fine reason as recorded in our system — surfaced on the new
      // CF.Fine Description column (BC). Fall back through the same
      // priority chain used on the on-screen Reason column.
      const fineDescription = (
        fine.reason
        || fine.fine_type
        || fine.violation
        || fine.fine_reason
        || fine.description
        || ''
      );
      const emirate = (lessee.emirate || lessee.location || fine.emirate || 'Dubai').toString().toLowerCase();
      const placeOfSupply = EMIRATE_TO_POS[emirate] || 'DU';
      // CF.Bike Plate Number should contain the FULL plate (e.g., "DXB 2-33654").
      // Prefer the golden plate when available, else fall back to the old plate.
      const fullBikePlate = (
        fine.bike_golden_plate_number
        || fine.bike_plate_number
        || fine.motorcycle_plate
        || fine.bike_plate
        || fine.plate_number
        || ''
      ).toString().trim();
      const base = {
        'Invoice Date': invDate,
        'Invoice Number': invNo,
        'Estimate Number': '',
        'Invoice Status': 'Draft',
        'Customer Name': fine.lessee_name || '',
        'VAT Treatment': vatTreatment,
        'Place Of Supply': placeOfSupply,
        'Tax Registration Number': trn,
        'Is Inclusive Tax': 'False',
        'Invoice Level Tax': '', 'Invoice Level Tax %': '', 'Invoice Level Tax Type': '', 'Invoice Level Tax Exemption Reason': '',
        'Due Date': dueDate, 'Expected Payment Date': '', 'PurchaseOrder': '',
        'Template Name': 'EZMS Invoice', 'Currency Code': 'AED', 'Exchange Rate': 1,
        'Discount Type': 'item_level', 'Is Discount Before Tax': 'TRUE',
        'Entity Discount Percent': 0, 'Entity Discount Amount': 0, 'Account': 'Sales',
        'Item Name': '', 'SKU': '', 'Quantity': 1, 'Usage unit': '',
        'Discount': 0, 'Discount Amount': 0, 'Expense Reference ID': '',
        'Item Type': 'Service', 'Adjustment': 0, 'Adjustment Description': 'Adjustment',
        'Sales person': '', 'Payment Terms': 10, 'Payment Terms Label': 'Due on date',
        'Shipping Charge': 0, 'Sales Order Number': '',
        'Notes': 'Thanks for your business.',
        'E-Commerce Operator Name': '',
        'Terms & Conditions': 'Payment Terms: Due upon presentation',
        'CF.Invoice Type': 'Fines',
        'CF.Fine Reference Number': fine.fine_number || fine.reference_number || '',
        'CF.Bike Plate Number': fullBikePlate,
        'CF.Fine Source': fine.fine_source || fine.source || fine.fine_by || '',
        // Rider the fine is attributed to — `fine_by_rider_name` is the
        // canonical field, `current_rider_name` is the fallback used
        // when a fine was raised without picking the rider yet.
        'CF.Rider Name': fine.fine_by_rider_name || fine.current_rider_name || fine.rider_name || '',
        'CF.Fine Description': fineDescription,
      };
      // Line 1 — the fine itself (out-of-scope, no VAT)
      rows.push({
        ...base,
        'Item Desc': fine.description || fine.violation || fine.fine_reason || 'Traffic Fine',
        'Item Price': Number(fine.amount || fine.fine_amount || 0),
        'Item Tax': '', 'Item Tax %': '', 'Item Tax Type': '',
        'Out of Scope Reason': 'uae_reimbursed_expense',
        'Item Tax Exemption Reason': 'OUTOFSCOPE',
      });
      // Line 2 — admin fee (Standard Rate 5%)
      const adminFee = Number(fine.admin_fees || fine.admin_fee || 0);
      if (adminFee > 0) {
        rows.push({
          ...base,
          'Item Desc': 'Admin / Service charges',
          'Item Price': adminFee,
          'Item Tax': 'Standard Rate', 'Item Tax %': 5, 'Item Tax Type': 'ItemAmount',
          'Out of Scope Reason': '', 'Item Tax Exemption Reason': '',
        });
      }
      // Line 3 — discount (negative Item Price) when present.
      const discount = Number(fine.discount || fine.discount_amount || 0);
      if (discount > 0) {
        rows.push({
          ...base,
          'Item Desc': 'Discount',
          'Item Price': -Math.abs(discount),
          'Item Tax': '', 'Item Tax %': '', 'Item Tax Type': '',
          'Out of Scope Reason': 'uae_reimbursed_expense',
          'Item Tax Exemption Reason': 'OUTOFSCOPE',
        });
      }
    });
    return rows;
  };

  // Import Fine Report Functions
  const fineDownloadColumns = [    { header: 'Fine #', key: 'fine_number', width: 15 },
    { header: 'Date', key: 'fine_date', width: 12 },
    { header: 'Plate', key: 'bike_plate_number', width: 15 },
    { header: 'Golden Plate', key: 'bike_golden_plate_number', width: 15, format: (v) => v || '-' },
    { header: 'Lessee', key: 'lessee_name', width: 22, format: (v) => v || '-' },
    { header: 'Current Rider', key: 'current_rider_name', width: 25 },
    { header: 'Fine by', key: 'fine_by_rider_name', width: 25 },
    { header: 'Reason', key: 'reason', width: 20, format: (v, row) => v || row.fine_type || '-' },
    { header: 'Amount', key: 'amount', width: 10, format: (v, row) => Number(v || row.fine_amount || 0).toFixed(2) },
    { header: 'Admin Fee', key: 'admin_fees', width: 10, format: (v, row) => Number(v || row.admin_fee || 0).toFixed(2) },
    { header: 'VAT (5% on Admin)', key: '_vat', width: 12, format: (_, row) => (Number(row.admin_fees || row.admin_fee || 0) * 0.05).toFixed(2) },
    { header: 'Total', key: '_total', width: 10, format: (_, row) => {
      const a = Number(row.amount || row.fine_amount || 0);
      const b = Number(row.admin_fees || row.admin_fee || 0);
      return (a + b + b * 0.05).toFixed(2);
    } },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'Source', key: 'source', width: 10 },
    { header: 'Authority Payment', key: 'authority_payment_status', width: 15, format: (v) => v || 'Not Paid' },
    { header: 'Authority Paid Date', key: 'authority_payment_date', width: 14, format: (v) => v || '-' },
  ];

  const handleDownloadFines = async (format) => {
    try {
      toast.info(`Preparing ${format.toUpperCase()} download...`);
      // Fetch ALL matching records (bypass pagination). Backend already
      // narrows by `search` + `status` — client-side extras (date range,
      // lessee, rider, bike) are applied below via `filterData` so the
      // downloaded file mirrors exactly what the user sees on screen.
      // Limit set high (1,000,000) so exports never truncate as the fleet grows.
      const params = new URLSearchParams();
      if (activeSearch) params.append('search', activeSearch);
      if (summaryFilter && summaryFilter !== 'all') params.append('status', summaryFilter);
      params.append('limit', '1000000');
      params.append('skip', '0');

      const res = await axios.get(`${API}/fines?${params.toString()}`, { headers: getAuthHeader() });
      let allFines = res.data;

      // Apply the on-screen filter chip set (date range + lessee + rider + bike).
      allFines = filterData(allFines, filters);

      // Human-readable filter summary embedded in filename + PDF footer.
      const parts = [];
      if (activeSearch) parts.push(`Search: "${activeSearch}"`);
      if (filters.fromDate || filters.toDate) parts.push(`Dates: ${filters.fromDate || '…'} → ${filters.toDate || '…'}`);
      if (filters.lessee) parts.push(`Lessee: ${filters.lessee}`);
      if (filters.rider) parts.push(`Rider: ${filters.rider}`);
      if (filters.bike) parts.push(`Bike: ${filters.bike}`);
      if (summaryFilter && summaryFilter !== 'all') parts.push(`Status: ${summaryFilter}`);
      const filterInfo = parts.length ? parts.join(' | ') : 'All records';

      // Compose a filename hint from the applied filters.
      const slug = [
        summaryFilter && summaryFilter !== 'all' ? summaryFilter : '',
        filters.lessee ? filters.lessee.replace(/\s+/g, '') : '',
        filters.bike ? String(filters.bike).replace(/\s+/g, '') : '',
        filters.fromDate ? filters.fromDate : '',
        filters.toDate && filters.toDate !== filters.fromDate ? `to_${filters.toDate}` : '',
      ].filter(Boolean).join('_');
      const fname = `Fines_Log_${new Date().toISOString().slice(0, 10)}${slug ? '_' + slug : ''}`;

      if (allFines.length === 0) {
        toast.warning('No fines match the current filters');
        return;
      }

      if (format === 'excel') {
        downloadExcel(allFines, fineDownloadColumns, fname, 'Fines');
      } else if (format === 'zoho') {
        // Zoho Books import format — one invoice header per fine, two
        // line items per invoice (fine + admin fee), plus a third line
        // when a discount exists. Fetch lessees once to resolve TRN +
        // Place of Supply since those aren't stored on the fine itself.
        let lesseeMap = {};
        let lesseeIdMap = {};
        try {
          const lRes = await axios.get(`${API}/lessees`, { headers: getAuthHeader() });
          // /api/lessees may return either a bare array OR {lessees: [...]}
          // — normalise both shapes so `.forEach` never crashes.
          const list = Array.isArray(lRes.data)
            ? lRes.data
            : (lRes.data?.lessees || lRes.data?.data || []);
          list.forEach(l => {
            // Lessee documents on this DB store the display name under
            // `company_name`. Older aliases (`name`, `lessee_name`,
            // `trade_name`) are kept as safety fallbacks. Register the
            // same lessee under every alias we can find so
            // `fine.lessee_name` — whatever it happens to hold — still
            // resolves.
            const aliases = [
              l.company_name,
              l.name,
              l.lessee_name,
              l.trade_name,
              l.display_name,
            ].filter(Boolean);
            aliases.forEach(alias => {
              const key = alias.toString().trim().toLowerCase();
              if (key) lesseeMap[key] = l;
            });
            if (l.id) lesseeIdMap[l.id] = l;
            if (l._id) lesseeIdMap[l._id] = l;
          });
        } catch (e) {
          console.warn('[zoho export] lessee fetch failed — TRN cols will be blank', e);
        }
        // Pass the id-map to the builder so it can resolve lessees when
        // the fine carries `lessee_id` (or older aliases) even if the
        // name column drifted.
        let zohoRows = [];
        try {
          zohoRows = buildZohoRows(allFines, lesseeMap, lesseeIdMap);
        } catch (e) {
          console.error('[zoho export] row builder crashed', e);
          toast.error(`Zoho export failed: ${e.message || 'row builder error'}`);
          return;
        }
        if (zohoRows.length === 0) {
          toast.warning('Nothing to export — check the current filters.');
          return;
        }
        downloadExcel(zohoRows, ZOHO_COLUMNS, `${fname}_ZOHO`, 'Zoho Fines Import');
      } else {
        downloadPDF(allFines, fineDownloadColumns, fname, 'Fines Log', filterInfo);
      }
      toast.success(`${format.toUpperCase()} downloaded (${allFines.length} record${allFines.length === 1 ? '' : 's'})`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    }
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportPreview(null);
    }
  };

  // ===== Lessee Monthly Summary Handlers =====
  const fetchLesseeSummary = async () => {
    setLesseeSummaryLoading(true);
    try {
      const res = await axios.get(`${API}/fines/lessee-monthly-summary`, { headers: getAuthHeader() });
      setLesseeSummary(res.data.summary || []);
    } catch (e) {
      toast.error('Failed to load summary');
    } finally {
      setLesseeSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (lesseeSummaryExpanded && lesseeSummary.length === 0) {
      fetchLesseeSummary();
    }
  }, [lesseeSummaryExpanded]);

  // ===== Rider Pay (Direct pay by Rider) Monthly Summary =====
  const fetchRiderPaySummary = async () => {
    setRiderPaySummaryLoading(true);
    try {
      const res = await axios.get(`${API}/fines/rider-pay-monthly-summary`, { headers: getAuthHeader() });
      setRiderPaySummary(res.data.summary || []);
      setRiderPaySummaryTotals(res.data.totals || null);
    } catch (e) {
      toast.error('Failed to load Rider Pay summary');
    } finally {
      setRiderPaySummaryLoading(false);
    }
  };

  useEffect(() => {
    if (riderPaySummaryExpanded && riderPaySummary.length === 0) {
      fetchRiderPaySummary();
    }
  }, [riderPaySummaryExpanded]);

  // ===== Monthly Audit Handlers =====
  const fetchMonthlyAudit = async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ group_by: auditGroupBy });
      if (auditYear) params.append('year', auditYear);
      if (auditMonth) params.append('month', auditMonth);
      const res = await axios.get(`${API}/fines/monthly-audit?${params}`, { headers: getAuthHeader() });
      setAuditData({ rows: res.data.rows || [], totals: res.data.totals || {} });
    } catch (e) {
      toast.error('Failed to load audit data');
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (auditExpanded) fetchMonthlyAudit();
  }, [auditExpanded, auditGroupBy, auditYear, auditMonth]);

  const handleExpandSummaryRow = async (lesseeName, month) => {
    const key = `${lesseeName}||${month}`;
    if (expandedSummaryRow === key) {
      setExpandedSummaryRow(null);
      setExpandedRowFines(null);
      return;
    }
    setExpandedSummaryRow(key);
    setExpandedRowFines(null);
    try {
      const res = await axios.get(`${API}/fines/lessee-month-details?lessee_name=${encodeURIComponent(lesseeName)}&month=${month}`, { headers: getAuthHeader() });
      setExpandedRowFines(res.data.fines || []);
    } catch (e) {
      toast.error('Failed to load details');
      setExpandedRowFines([]);
    }
  };

  const handleMarkLesseeMonthPaid = (lesseeName, month) => {
    const monthLabel = month !== 'Unknown' ? new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : month;
    openPaidProofDialog({
      mode: 'lessee_month',
      lesseeName,
      month,
      contextLabel: `${lesseeName} — ${monthLabel}`,
    });
  };

  const handleDownloadLesseeSummary = (format) => {
    if (lesseeSummary.length === 0) { toast.error('No data to download'); return; }
    const data = lesseeSummary.map(row => ({
      'Lessee': row.lessee_name,
      'Month': row.month !== 'Unknown' ? new Date(row.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Unknown',
      'No. of Fines': row.count,
      'Total Amount (AED)': row.total_amount.toFixed(2),
      'Paid': row.paid_count,
      'Status': row.status === 'paid' ? 'Paid' : row.status === 'partial' ? 'Partially Paid' : 'Unpaid'
    }));
    
    if (format === 'excel') {
      downloadExcel(data, 'Lessee_Monthly_Fines_Summary', 'Summary');
    } else {
      downloadPDF(data, 'Lessee_Monthly_Fines_Summary', 'Lessee-wise Monthly Fines Summary');
    }
  };
  // ===== End Lessee Summary Handlers =====

  const handleImportFineReport = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('mode', importMode);

      const response = await axios.post(
        `${API}/fines/import-fine-report`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const { imported, skipped, updated, errors } = response.data;
      
      if (imported > 0 || updated > 0) {
        toast.success(`Successfully imported ${imported} fines${updated > 0 ? `, updated ${updated}` : ''}${skipped > 0 ? `, skipped ${skipped} duplicates` : ''}`);
      }
      
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} records had errors`);
      }

      setImportDialogOpen(false);
      setImportFile(null);
      setImportPreview(null);
      fetchData();

      // Auto-prompt: offer to backfill WhatsApp for the freshly imported rows
      // (in case Meta template rate-limits stagger delivery or a rider was
      // temporarily opted-out at insert-time).
      if (imported > 0) {
        setTimeout(() => {
          toast(
            (t) => (
              <div className="flex flex-col gap-2">
                <div className="text-sm font-medium">Send WhatsApp for the {imported} newly-imported fine{imported === 1 ? '' : 's'}?</div>
                <div className="text-xs text-slate-600">The auto-trigger already fires on insert. Click below only if you want to re-fire for any that got missed.</div>
                <div className="flex gap-2 mt-1">
                  <Button
                    size="sm"
                    className="bg-sky-600 hover:bg-sky-700 h-7 text-xs"
                    onClick={() => {
                      toast.dismiss(t.id);
                      handleBackfillWhatsApp();
                    }}
                  >
                    Yes, backfill
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toast.dismiss(t.id)}>Skip</Button>
                </div>
              </div>
            ),
            { duration: 15000 },
          );
        }, 800);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(formatErrorMessage(error, 'Failed to import fine report'));
    } finally {
      setImporting(false);
    }
  };

  const downloadFineReportTemplate = async () => {
    // Download Excel template from backend
    try {
      const response = await axios.get(`${API}/fines/import-template`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Fine_Report_Import_Template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'paid' || statusLower === 'yes') {
      return 'bg-green-100 text-green-800';
    }
    if (statusLower === 'recovered_from_salary') {
      return 'bg-blue-100 text-blue-800';
    }
    return 'bg-red-100 text-red-800';
  };

  // Fine sources list
  const fineSources = [
    'Dubai RTA',
    'RTA (Parking Fines)',
    'Dubai Police',
    'DUBAI POLICE',
    'Sharjah Municipality',
    'Sharjah Police',
    'SALIK',
    'Parkin',
    'Other'
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="fines-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">{t('fines.title')}</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">{t('fines.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {user?.role !== 'Account Manager' && activeTab === 'fines' && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-lime-600 to-green-600" size={isMobile ? "sm" : "default"} data-testid="add-fine-btn">
                <Plus className="w-4 h-4 mr-2" />
                {isMobile ? 'Add' : t('fines.addFine')}
              </Button>
            </DialogTrigger>
          <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-3xl'} max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle>{editingId ? t('fines.editFine') : t('fines.addFine')}</DialogTitle>
              <DialogDescription>
                Select motorcycle and rider from your registered fleet. Plate number and lessee will be auto-filled.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Fine/Ticket Number Field */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Fine / Ticket Number
                    <span className="text-xs text-blue-600">(Official ID from authority)</span>
                  </Label>
                  <Input
                    value={formData.fine_number}
                    onChange={(e) => setFormData({ ...formData, fine_number: e.target.value })}
                    placeholder="e.g., TKT-2024-123456"
                    data-testid="fine-number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fine Date *</Label>
                  <Input
                    type="date"
                    value={formData.fine_date}
                    onChange={(e) => setFormData({ ...formData, fine_date: e.target.value })}
                    required
                    data-testid="fine-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fine Time *</Label>
                  <Input
                    type="time"
                    value={formData.fine_time}
                    onChange={(e) => setFormData({ ...formData, fine_time: e.target.value })}
                    required
                    data-testid="fine-time"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Select Motorcycle from Registered Fleet *</Label>
                  <SearchableSelect
                    options={motorcycles.map((bike) => ({
                      value: bike.id,
                      label: bike.golden_plate_number ? `${bike.plate_number} / ${bike.golden_plate_number}` : bike.plate_number,
                      searchText: `${bike.plate_number} ${bike.golden_plate_number || ''} ${bike.model_name} ${bike.model_version} ${bike.chassis_number || ''} ${bike.lessee_name || ''}`,
                      metadata: {
                        'Model': `${bike.model_name} ${bike.model_version}`,
                        'Color': bike.color,
                        'Chassis': bike.chassis_number,
                        'Lessee': bike.lessee_name || '-'
                      }
                    }))}
                    value={formData.bike_id}
                    onValueChange={handleBikeSelect}
                    placeholder="Choose from registered motorcycles..."
                    emptyMessage="No motorcycles registered"
                  />
                  {formData.bike_plate_number && (
                    <div className="flex items-center gap-4 p-3 bg-green-50 border border-green-200 rounded-lg mt-2">
                      <div className="flex-1">
                        <div className="text-xs text-green-600 mb-1">Selected Plate:</div>
                        <PlatePair oldPlate={formData.bike_plate_number} goldenPlate={formData.bike_golden_plate_number} />
                        {formData.lessee_name && (
                          <p className="text-xs text-green-700 mt-1">
                            <span className="text-green-600">Lessee:</span> {formData.lessee_name}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Select Rider *</Label>
                  <SearchableSelect
                    options={riders.map((rider) => ({
                      value: rider.id,
                      label: rider.name,
                      searchText: `${rider.name} ${rider.emirates_id} ${rider.mobile_uae || ''} ${rider.license_number || ''}`,
                      metadata: {
                        'Emirates ID': rider.emirates_id,
                        'Mobile': rider.mobile_uae,
                        'License': rider.license_number,
                        'Platform': rider.platform
                      }
                    }))}
                    value={formData.rider_id}
                    onValueChange={handleRiderSelect}
                    placeholder="Select rider"
                    emptyMessage="No riders found"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fine Source *</Label>
                  <Select value={formData.source} onValueChange={(value) => setFormData({ ...formData, source: value })}>
                    <SelectTrigger data-testid="fine-source">
                      <SelectValue placeholder="Select fine source" />
                    </SelectTrigger>
                    <SelectContent>
                      {fineSources.map((source) => (
                        <SelectItem key={source} value={source}>{source}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 col-span-2">
                  {formData.rider_name && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-medium text-green-900">
                        <span className="text-xs text-green-600">Selected Rider:</span> {formData.rider_name}
                      </p>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Fine Amount (Dh) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    required
                    data-testid="fine-amount"
                  />
                  {adminFeeConfig?.fines && (
                    <p className="text-xs text-slate-500">
                      Admin fee: {adminFeeConfig.fines.fee_type === 'percentage' 
                        ? `${adminFeeConfig.fines.percentage}% of fine amount` 
                        : `Fixed Dh ${adminFeeConfig.fines.fixed_amount}`}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Admin Fees (Dh)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.admin_fees}
                    onChange={(e) => setFormData({ ...formData, admin_fees: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    data-testid="fine-admin-fees"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Reason / Type of Fine *</Label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="e.g., Speeding, Red light violation, Parking violation"
                    required
                    data-testid="fine-reason"
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Select value={formData.city} onValueChange={(value) => setFormData({ ...formData, city: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Dubai">Dubai</SelectItem>
                      <SelectItem value="Sharjah">Sharjah</SelectItem>
                      <SelectItem value="Abu Dhabi">Abu Dhabi</SelectItem>
                      <SelectItem value="Ajman">Ajman</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger data-testid="fine-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">{t('fines.pending')}</SelectItem>
                      <SelectItem value="paid">{t('fines.paid')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <FileUpload
                  label="Upload Ticket Photo"
                  value={formData.ticket_photo_url}
                  onChange={(url) => setFormData({ ...formData, ticket_photo_url: url })}
                  accept="image/*,.pdf"
                  API={API}
                  getAuthHeader={getAuthHeader}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-lime-600 to-green-600" data-testid="submit-fine-btn">
                  {editingId ? 'Update' : 'Add'} Fine
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Import Fine Report Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-lg'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-red-600" />
              Import Fine Report
            </DialogTitle>
            <DialogDescription>
              Upload the Excel file exported from the authority system (Dubai RTA, Dubai Police, etc.)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Upload */}
            <div className="space-y-2">
              <Label>Select Fine Report File <span className="text-gray-500 font-normal">(.xls, .xlsx)</span></Label>
              <Input
                type="file"
                accept=".xls,.xlsx"
                onChange={handleImportFileChange}
                className="cursor-pointer"
              />
              <p className="text-xs text-gray-500">Accepted formats: Excel files (.xls, .xlsx)</p>
              {importFile && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  {importFile.name}
                </p>
              )}
            </div>

            {/* Duplicate Handling */}
            <div className="space-y-2">
              <Label>If duplicate ticket numbers are found:</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip duplicates (keep existing)</SelectItem>
                  <SelectItem value="update">Update existing records</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Info Box */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Expected File Format:</p>
                  <ul className="list-disc list-inside mt-1 text-xs space-y-0.5">
                    <li>Excel file (.xls or .xlsx) from authority system</li>
                    <li>Headers in row 3: Plate Number, Ticket Number, Ticket Date, etc.</li>
                    <li>System will match plates to registered motorcycles</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Download Template */}
            <Button
              variant="outline"
              size="sm"
              onClick={downloadFineReportTemplate}
              className="w-full gap-2"
            >
              <Download className="w-4 h-4" />
              Download Template
            </Button>

            {/* Actions */}
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleImportFineReport}
                disabled={!importFile || importing}
                className="bg-gradient-to-r from-red-600 to-orange-600"
              >
                {importing ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Fines
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fines list (draft/approval tabs removed — legacy Pending Approval flow deprecated) */}
      <div className="w-full mt-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Total Paid Card */}
            <Card 
              className={`cursor-pointer transition-all duration-200 ${summaryFilter === 'paid' ? 'ring-2 ring-green-500 shadow-lg' : 'hover:shadow-md'}`}
              onClick={() => { setSummaryFilter(summaryFilter === 'paid' ? null : 'paid'); setCurrentPage(1); fetchData(1, activeSearch, summaryFilter === 'paid' ? null : 'paid'); }}
              data-testid="paid-summary-card"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Total Paid</p>
                    <p className="text-2xl font-bold text-green-600">
                      <CurrencyDisplay amount={summaryStats.totalPaid} />
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{summaryStats.paidCount} fine(s)</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Total Unpaid Card */}
            <Card 
              className={`cursor-pointer transition-all duration-200 ${summaryFilter === 'unpaid' ? 'ring-2 ring-red-500 shadow-lg' : 'hover:shadow-md'}`}
              onClick={() => { setSummaryFilter(summaryFilter === 'unpaid' ? null : 'unpaid'); setCurrentPage(1); fetchData(1, activeSearch, summaryFilter === 'unpaid' ? null : 'unpaid'); }}
              data-testid="unpaid-summary-card"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Total Unpaid</p>
                    <p className="text-2xl font-bold text-red-600">
                      <CurrencyDisplay amount={summaryStats.totalUnpaid} />
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{summaryStats.unpaidCount} fine(s)</p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-full">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Highest Fine Bike Card */}
            <Card 
              className="cursor-pointer transition-all duration-200 hover:shadow-md"
              onClick={() => setShowBikeRankingDialog(true)}
              data-testid="highest-fine-card"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Highest Fine Vehicle</p>
                    {highestFineBike ? (
                      <>
                        <p className="text-lg font-bold text-purple-600">{highestFineBike.plate}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {highestFineBike.count} fine(s) - <CurrencyDisplay amount={highestFineBike.total} />
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-400">No fines recorded</p>
                    )}
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rider Pay Card */}
            <Card
              className={`cursor-pointer transition-all duration-200 ${summaryFilter === 'rider_pay' ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'}`}
              onClick={() => { setSummaryFilter(summaryFilter === 'rider_pay' ? null : 'rider_pay'); setCurrentPage(1); fetchData(1, activeSearch, summaryFilter === 'rider_pay' ? null : 'rider_pay'); }}
              data-testid="rider-pay-summary-card"
              title="Fines flagged 'Rider Pays Directly' — settled outside payroll"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Rider Pay</p>
                    <p className="text-2xl font-bold text-blue-600">
                      <CurrencyDisplay amount={summaryStats.riderPayTotal || 0} />
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{summaryStats.riderPayCount || 0} fine(s)</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Active Filter Indicator */}
          {summaryFilter && (
            <div className="flex items-center gap-2 p-2 mb-4 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-sm text-blue-700">
                Showing: <strong>{summaryFilter === 'paid' ? 'Paid' : summaryFilter === 'rider_pay' ? 'Rider Pay' : 'Unpaid'}</strong> fines only
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setSummaryFilter(null); setCurrentPage(1); fetchData(1, activeSearch, null); }}
                className="h-6 px-2 text-blue-600 hover:text-blue-800"
              >
                Clear Filter
              </Button>
            </div>
          )}
          
          {/* Bike Ranking Dialog */}
          <Dialog open={showBikeRankingDialog} onOpenChange={setShowBikeRankingDialog}>
            <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-3xl'} max-h-[80vh] overflow-y-auto`}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Fine Rankings by Vehicle
                </DialogTitle>
                <DialogDescription>
                  Motorcycles ranked by total fines (highest to lowest)
                </DialogDescription>
              </DialogHeader>
              
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <div 
                  className="table-scroll-container"
                  style={{
                    maxHeight: '400px',
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-12 font-semibold">Rank</TableHead>
                        <TableHead className="font-semibold">Plate Number</TableHead>
                        <TableHead className="font-semibold text-center">Total Fines</TableHead>
                        <TableHead className="font-semibold text-right">Total Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedBikesByFines.map((bike, index) => (
                        <TableRow key={bike.plate} className={index === 0 ? 'bg-purple-50' : ''}>
                          <TableCell>
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                              index === 0 ? 'bg-purple-600 text-white' : 
                              index === 1 ? 'bg-purple-400 text-white' : 
                              index === 2 ? 'bg-purple-300 text-purple-800' : 
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {index + 1}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">{bike.plate}</TableCell>
                          <TableCell className="text-center">{bike.count}</TableCell>
                          <TableCell className="text-right font-semibold">
                            <CurrencyDisplay amount={bike.total} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <Card className="border-0 shadow-lg">
            <CardHeader className="cursor-pointer" onClick={() => setLesseeSummaryExpanded(!lesseeSummaryExpanded)} data-testid="lessee-summary-toggle">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-green-600" />
                  Lessee-wise Monthly Summary
                  {lesseeSummary.length > 0 && <Badge variant="secondary" className="ml-2">{lesseeSummary.length}</Badge>}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {lesseeSummaryExpanded && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={(e) => { e.stopPropagation(); handleDownloadLesseeSummary('excel'); }} data-testid="download-lessee-excel">
                        <FileSpreadsheet className="w-3 h-3" /> Excel
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={(e) => { e.stopPropagation(); handleDownloadLesseeSummary('pdf'); }} data-testid="download-lessee-pdf">
                        <Download className="w-3 h-3" /> PDF
                      </Button>
                    </>
                  )}
                  <span className={`transition-transform ${lesseeSummaryExpanded ? 'rotate-180' : ''}`}>▼</span>
                </div>
              </div>
            </CardHeader>
            {lesseeSummaryExpanded && (
              <CardContent className="pt-0">
                {lesseeSummaryLoading ? (
                  <div className="flex items-center justify-center py-6"><Loader className="w-5 h-5 animate-spin text-green-600" /><span className="ml-2 text-sm text-slate-500">Loading summary...</span></div>
                ) : lesseeSummary.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No fines data available</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Lessee</TableHead>
                          <TableHead>Month</TableHead>
                          <TableHead className="text-center">Fines</TableHead>
                          <TableHead className="text-right">Total Amount</TableHead>
                          <TableHead className="text-right text-green-700 whitespace-normal break-words leading-tight align-bottom min-w-[120px]" title="Sum of fines deducted from rider salaries in approved payrolls for this lessee/month">Recovered (Rider&rsquo;s Salary)</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lesseeSummary.map((row, idx) => {
                          const monthLabel = row.month !== 'Unknown' ? new Date(row.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Unknown';
                          const isExpanded = expandedSummaryRow === `${row.lessee_name}||${row.month}`;
                          return (
                            <React.Fragment key={idx}>
                              <TableRow 
                                className="cursor-pointer hover:bg-slate-50 text-sm"
                                onClick={() => handleExpandSummaryRow(row.lessee_name, row.month)}
                                data-testid={`lessee-summary-row-${idx}`}
                              >
                                <TableCell className="font-medium">{row.lessee_name}</TableCell>
                                <TableCell>{monthLabel}</TableCell>
                                <TableCell className="text-center">{row.count}</TableCell>
                                <TableCell className="text-right font-semibold"><CurrencyDisplay amount={row.total_amount} /></TableCell>
                                <TableCell className="text-right font-mono text-sm text-green-700">
                                  {(row.recovered_from_salary || 0) > 0 ? (
                                    <CurrencyDisplay amount={row.recovered_from_salary} />
                                  ) : (
                                    <span className="text-slate-300">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={row.status === 'paid' ? 'default' : row.status === 'partial' ? 'secondary' : 'destructive'}
                                    className={row.status === 'paid' ? 'bg-green-100 text-green-700' : row.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                                    {row.status === 'paid' ? 'Paid' : row.status === 'partial' ? `${row.paid_count}/${row.count} Paid` : 'Unpaid'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                  {row.status !== 'paid' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 px-2 text-[10px] bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                                      onClick={() => handleMarkLesseeMonthPaid(row.lessee_name, row.month)}
                                      data-testid={`mark-paid-${idx}`}
                                    >
                                      <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Paid
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                              {isExpanded && (
                                <TableRow>
                                  <TableCell colSpan={7} className="p-0 bg-slate-50">
                                    {expandedRowFines === null ? (
                                      <div className="flex items-center justify-center py-4"><Loader className="w-4 h-4 animate-spin" /></div>
                                    ) : (
                                      <div className="p-3 max-h-[300px] overflow-y-auto">
                                        <Table>
                                          <TableHeader>
                                            <TableRow className="bg-white">
                                              <TableHead className="text-xs">Ticket No.</TableHead>
                                              <TableHead className="text-xs">Date</TableHead>
                                              <TableHead className="text-xs">Plate</TableHead>
                                              <TableHead className="text-xs">Rider</TableHead>
                                              <TableHead className="text-xs text-right">Amount</TableHead>
                                              <TableHead className="text-xs text-center">Status</TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {expandedRowFines.map((f) => (
                                              <TableRow key={f.id} className="text-xs">
                                                <TableCell>{f.fine_number || f.ticket_number || '-'}</TableCell>
                                                <TableCell>{f.fine_date || f.date_of_fine || '-'}</TableCell>
                                                <TableCell><PlatePair oldPlate={f.bike_plate_number || f.motorcycle_plate} goldenPlate={f.bike_golden_plate_number} compact /></TableCell>
                                                <TableCell>{f.rider_name || '-'}</TableCell>
                                                <TableCell className="text-right"><CurrencyDisplay amount={(f.fine_amount || f.amount || 0) + (f.admin_fees || f.admin_fee || 0)} /></TableCell>
                                                <TableCell className="text-center">
                                                  <Badge variant="outline" className={f.status === 'paid' ? 'text-green-600' : 'text-red-600'}>
                                                    {f.status || 'pending'}
                                                  </Badge>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </div>
                                    )}
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* ===== Rider Pay (Direct pay by Rider) Monthly Summary ===== */}
          <Card className="border-0 shadow-lg" data-testid="rider-pay-summary-card">
            <CardHeader
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => setRiderPaySummaryExpanded(!riderPaySummaryExpanded)}
              data-testid="rider-pay-summary-toggle"
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-teal-600" />
                  Rider Pay — Monthly Summary
                  {riderPaySummary.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{riderPaySummary.length}</Badge>
                  )}
                  <Badge variant="outline" className="text-[10px] border-teal-300 text-teal-700">
                    Direct Pay by Rider
                  </Badge>
                </CardTitle>
                <div className="flex items-center gap-2">
                  {riderPaySummaryExpanded && riderPaySummaryTotals && (
                    <span className="text-xs text-slate-600" data-testid="rider-pay-summary-totals">
                      Fines: <span className="font-semibold text-teal-700">AED {Number(riderPaySummaryTotals.fines_total || 0).toFixed(2)}</span>
                      {' · '}Payroll: <span className="font-semibold text-blue-700">AED {Number(riderPaySummaryTotals.payroll_total || 0).toFixed(2)}</span>
                      {Math.abs(riderPaySummaryTotals.variance || 0) > 0.01 && (
                        <span className="ml-2 text-red-600">⚠ Variance: AED {Number(riderPaySummaryTotals.variance).toFixed(2)}</span>
                      )}
                    </span>
                  )}
                  <span className={`transition-transform ${riderPaySummaryExpanded ? 'rotate-180' : ''}`}>▼</span>
                </div>
              </div>
            </CardHeader>
            {riderPaySummaryExpanded && (
              <CardContent className="pt-0">
                {riderPaySummaryLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader className="w-5 h-5 animate-spin text-teal-600" />
                    <span className="ml-2 text-sm text-slate-500">Loading summary…</span>
                  </div>
                ) : riderPaySummary.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">
                    No fines marked as &ldquo;Direct pay by Rider&rdquo; yet.
                  </p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Rider</TableHead>
                          <TableHead>Month</TableHead>
                          <TableHead className="text-center">Fines</TableHead>
                          <TableHead className="text-right">Total (AED)</TableHead>
                          <TableHead className="text-right text-slate-600">Admin (AED)</TableHead>
                          <TableHead
                            className="text-right text-blue-700 min-w-[130px]"
                            title="rider_pay_fines_total captured on the executed payroll for this rider/month"
                          >
                            Payroll Captured (AED)
                          </TableHead>
                          <TableHead className="text-center">Reconciliation</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {riderPaySummary.map((row, idx) => {
                          const monthLabel =
                            row.month !== 'Unknown'
                              ? new Date(row.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                              : 'Unknown';
                          const variance = (row.total_amount || 0) - (row.payroll_rider_pay_total || 0);
                          return (
                            <TableRow key={idx} className="text-sm hover:bg-slate-50" data-testid={`rider-pay-summary-row-${idx}`}>
                              <TableCell>
                                <div className="font-medium">{row.rider_name}</div>
                                {row.bike_plates && row.bike_plates.length > 0 && (
                                  <div className="text-[10px] text-slate-500">{row.bike_plates.join(', ')}</div>
                                )}
                                {row.remarks && row.remarks.length > 0 && (
                                  <div className="text-[10px] text-teal-600 italic" title={row.remarks.join(' / ')}>
                                    {row.remarks[0].substring(0, 40)}{row.remarks[0].length > 40 ? '…' : ''}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>{monthLabel}</TableCell>
                              <TableCell className="text-center font-mono">{row.count}</TableCell>
                              <TableCell className="text-right font-semibold font-mono text-teal-700">
                                {Number(row.total_amount).toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-slate-600">
                                {Number(row.admin_charges).toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-blue-700">
                                {row.payroll_captured ? (
                                  Number(row.payroll_rider_pay_total).toFixed(2)
                                ) : (
                                  <span className="text-slate-300">Not in payroll</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {!row.payroll_captured ? (
                                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                                    Pending Payroll
                                  </Badge>
                                ) : row.reconciled ? (
                                  <Badge className="bg-green-100 text-green-700 text-[10px]">
                                    <CheckCircle2 className="w-3 h-3 mr-1" /> Reconciled
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]" title={`Variance: ${variance.toFixed(2)} AED`}>
                                    ⚠ Mismatch
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            )}
          </Card>


          {/* ===== Monthly Audit Panel ===== */}
          <Card className="border-0 shadow-lg" data-testid="fines-audit-card">
            <CardHeader
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => setAuditExpanded(!auditExpanded)}
              data-testid="audit-toggle"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {auditExpanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    Monthly Fines Audit
                  </CardTitle>
                  <Badge variant="outline" className="text-[10px]">Reconciliation</Badge>
                </div>
                {auditExpanded && (
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <select value={auditGroupBy} onChange={(e) => setAuditGroupBy(e.target.value)} className="h-8 text-xs rounded-md border border-slate-200 px-2 bg-white" data-testid="audit-group-by">
                      <option value="month">Group: Month</option>
                      <option value="lessee">Group: Lessee</option>
                      <option value="rider">Group: Rider</option>
                    </select>
                    <select value={auditYear} onChange={(e) => setAuditYear(e.target.value)} className="h-8 text-xs rounded-md border border-slate-200 px-2 bg-white" data-testid="audit-year">
                      <option value="">All Years</option>
                      {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select value={auditMonth} onChange={(e) => setAuditMonth(e.target.value)} className="h-8 text-xs rounded-md border border-slate-200 px-2 bg-white" data-testid="audit-month">
                      <option value="">All Months</option>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <option key={m} value={m}>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]}</option>
                      ))}
                    </select>
                    <Button size="sm" variant="ghost" onClick={fetchMonthlyAudit} className="h-8 text-xs gap-1" title="Refresh">
                      <RefreshCw className={`w-3.5 h-3.5 ${auditLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            {auditExpanded && (
              <CardContent className="space-y-3">
                {auditLoading ? (
                  <div className="flex items-center justify-center py-8 text-slate-500"><Loader className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
                ) : (
                  <>
                    {/* Grand-total KPI strip */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                      {[
                        { label: 'Generated', val: auditData.totals.generated, color: 'bg-blue-50 border-blue-200 text-blue-700' },
                        { label: 'Claimed to Lessee', val: auditData.totals.claimed, color: 'bg-purple-50 border-purple-200 text-purple-700' },
                        { label: "Recovered (Rider's Salary)", val: auditData.totals.recovered, color: 'bg-green-50 border-green-200 text-green-700' },
                        { label: 'Paid by Rider', val: auditData.totals.rider_paid, color: 'bg-teal-50 border-teal-200 text-teal-700' },
                        { label: 'Pending', val: auditData.totals.pending, color: 'bg-amber-50 border-amber-200 text-amber-800' },
                        { label: 'Admin Charges', val: auditData.totals.admin, color: 'bg-slate-50 border-slate-200 text-slate-700' },
                      ].map(c => (
                        <div key={c.label} className={`rounded-md border ${c.color} px-3 py-2`}>
                          <p className="text-[10px] uppercase tracking-tight font-medium whitespace-normal break-words leading-snug min-h-[28px]">{c.label}</p>
                          <p className="text-lg font-bold">AED {Number(c.val || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                        </div>
                      ))}
                    </div>
                    {/* Audit rows */}
                    {auditData.rows.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">No fines match this period.</div>
                    ) : (
                      <div className="overflow-x-auto rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="whitespace-nowrap">{auditGroupBy === 'month' ? 'Month' : auditGroupBy === 'lessee' ? 'Lessee · Month' : 'Rider · Month'}</TableHead>
                              <TableHead className="text-right">Generated</TableHead>
                              <TableHead className="text-right text-purple-700 whitespace-normal break-words leading-tight align-bottom min-w-[110px]">Claimed to Lessee</TableHead>
                              <TableHead className="text-right text-green-700 whitespace-normal break-words leading-tight align-bottom min-w-[120px]">Recovered (Rider&rsquo;s Salary)</TableHead>
                              <TableHead className="text-right text-teal-700 whitespace-normal break-words leading-tight align-bottom min-w-[90px]">Rider Paid</TableHead>
                              <TableHead className="text-right text-amber-700">Pending</TableHead>
                              <TableHead className="text-right">Admin Chg.</TableHead>
                              <TableHead className="text-right">Variance</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {auditData.rows.map(r => (
                              <TableRow key={r.key} className="hover:bg-slate-50">
                                <TableCell className="font-medium whitespace-nowrap">{r.label}</TableCell>
                                <TableCell className="text-right font-mono text-sm">AED {Number(r.generated_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-[10px] text-slate-400">({r.generated_count})</span></TableCell>
                                <TableCell className="text-right font-mono text-sm text-purple-700">{r.claimed_amount > 0 ? `AED ${Number(r.claimed_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}</TableCell>
                                <TableCell className="text-right font-mono text-sm text-green-700">
                                  {r.recovered_salary_amount > 0 ? (
                                    <span title={r.inferred_recovered_amount > 0 ? `AED ${Number(r.inferred_recovered_amount).toFixed(2)} matched against approved-payroll deductions for this rider+month` : 'Recovered via approved payroll deduction'}>
                                      AED {Number(r.recovered_salary_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                    </span>
                                  ) : <span className="text-slate-300">—</span>}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm text-teal-700">
                                  {r.rider_paid_amount > 0 ? (
                                    <span title="Confirmed via Mark-Paid-With-Proof dialog with proof file uploaded">
                                      AED {Number(r.rider_paid_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                    </span>
                                  ) : <span className="text-slate-300">—</span>}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm text-amber-700">{r.pending_amount > 0 ? `AED ${Number(r.pending_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}</TableCell>
                                <TableCell className="text-right font-mono text-sm">AED {Number(r.admin_charges).toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                                <TableCell className={`text-right font-mono text-sm ${Math.abs(r.variance) > 1 ? 'text-red-600 font-bold' : 'text-green-600'}`} title={Math.abs(r.variance) > 1 ? 'Buckets don\u2019t sum to Generated' : 'Reconciled'}>
                                  {Math.abs(r.variance) > 1 ? `⚠ ${r.variance > 0 ? '+' : ''}${r.variance.toFixed(2)}` : '✓'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      <strong>4-bucket classification</strong> (mutually exclusive — Generated = Claimed + Recovered + Rider Paid + Pending):
                      <br />
                      <span className="text-green-700">• <strong>Recovered (Rider&rsquo;s Salary)</strong></span> — fine amount deducted from rider salary in an approved monthly payroll batch.
                      <br />
                      <span className="text-teal-700">• <strong>Paid by Rider</strong></span> — explicitly marked via the Mark-Paid-With-Proof dialog with a proof file uploaded. Status flag alone is not enough.
                      <br />
                      <span className="text-purple-700">• <strong>Claimed to Lessee</strong></span> — fine attributed to an <em>external</em> lessee company (lessee_type=&quot;external&quot;). Internal lessees recover via rider salary instead.
                      <br />
                      <span className="text-amber-700">• <strong>Pending</strong></span> — fines on internal lessees not yet recovered, or fines without lessee attribution. Use the Mark-Paid dialog to confirm a source and move them to the right bucket.
                    </p>
                  </>
                )}
              </CardContent>
            )}
          </Card>



          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <CardTitle>Traffic Fines List ({sortedFines.length}{totalCount > sortedFines.length ? ` of ${totalCount}` : ''})</CardTitle>
                {user?.role !== 'Account Manager' && (
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className="gap-2"
                    >
                      <Filter className="w-4 h-4" />
                      {isMobile ? (showFilters ? 'Hide' : 'Filter') : (showFilters ? 'Hide Filters' : 'Show Filters')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setImportDialogOpen(true)}
                      className="gap-2 bg-gradient-to-r from-red-50 to-orange-50 border-red-200 hover:bg-red-100"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-red-600" />
                      {!isMobile && 'Import Fine Report'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => bulkTicketPhotoInputRef.current?.click()}
                      disabled={bulkPhotoUploading}
                      className="gap-2 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 hover:bg-emerald-100"
                      data-testid="bulk-ticket-photo-btn"
                    >
                      <Camera className="w-4 h-4 text-emerald-700" />
                      {!isMobile && (bulkPhotoUploading ? 'Uploading…' : 'Bulk Upload Ticket Photos')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBackfillWhatsApp}
                      disabled={backfilling}
                      className="gap-2 bg-gradient-to-r from-sky-50 to-blue-50 border-sky-200 hover:bg-sky-100"
                      data-testid="backfill-whatsapp-btn"
                      title="Send fine_notification WhatsApp for fines imported in the last 72 hours that never got a notification"
                    >
                      <MessageCircle className="w-4 h-4 text-sky-700" />
                      {!isMobile && (backfilling ? 'Sending…' : 'Backfill WhatsApp')}
                    </Button>
                    <input
                      ref={bulkTicketPhotoInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={handleBulkTicketPhotoUpload}
                      data-testid="bulk-ticket-photo-input"
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1" data-testid="download-fines-btn">
                          <Download className="w-4 h-4" />
                          {!isMobile && 'Download'}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDownloadFines('excel')} className="cursor-pointer">
                          <FileSpreadsheet className="mr-2 h-4 w-4 text-green-600" />
                          Download Excel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadFines('zoho')} className="cursor-pointer" data-testid="download-zoho-format-btn">
                          <FileSpreadsheet className="mr-2 h-4 w-4 text-orange-600" />
                          Download in Zoho format
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadFines('pdf')} className="cursor-pointer">
                          <FileText className="mr-2 h-4 w-4 text-red-600" />
                          Download PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Server-Side Search Bar */}
              <div className="flex gap-2 mb-4" data-testid="fines-search-bar">
                <div className="relative flex-1">
                  {isSearching ? (
                    <Loader className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  )}
                  <Input
                    type="text"
                    placeholder="Search fines (plate, rider, ticket no, violation...)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    className="pl-10"
                    data-testid="fines-search-input"
                  />
                  {searchQuery && (
                    <button 
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="fines-search-clear"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Button 
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="bg-lime-600 hover:bg-lime-700 text-white px-4"
                  data-testid="fines-search-button"
                >
                  <Search className="w-4 h-4 mr-1" />
                  Search
                </Button>
              </div>
              
              {/* Bulk Actions Bar */}
              {selectedFines.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <span className="text-sm text-blue-800 font-medium">
                    {selectedFines.length} fine(s) selected
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkStatusChange('paid')}
                      className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                      data-testid="bulk-mark-paid-btn"
                    >
                      Mark as Paid
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkStatusChange('unpaid')}
                      className="bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                      data-testid="bulk-mark-unpaid-btn"
                    >
                      Mark as Unpaid
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkStatusChange('recovered_from_salary')}
                      className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                      data-testid="bulk-mark-recovered-btn"
                    >
                      Recovered from Salary
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedFines([])}
                      className="text-slate-600"
                    >
                      Clear Selection
                    </Button>
                  </div>
                </div>
              )}
              
              {showFilters && (
                <div className="mb-6 p-3 sm:p-4 bg-slate-50 rounded-lg space-y-4">
                  <DateRangeFilter
                    fromDate={filters.fromDate}
                    toDate={filters.toDate}
                    onFromDateChange={(val) => setFilters({ ...filters, fromDate: val })}
                    onToDateChange={(val) => setFilters({ ...filters, toDate: val })}
                  />
                  <SearchFilters
                    lessees={lessees}
                    riders={riders}
                    motorcycles={motorcycles}
                    selectedLessee={filters.lessee}
                    selectedRider={filters.rider}
                    selectedBike={filters.bike}
                    onLesseeChange={(val) => setFilters({ ...filters, lessee: val })}
                    onRiderChange={(val) => setFilters({ ...filters, rider: val })}
                    onBikeChange={(val) => setFilters({ ...filters, bike: val })}
                  />
                  {/* Status filter — synced with the top summary cards so
                      changing one reflects in the other. */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-slate-700 mb-1 block">Filter by Status</Label>
                      <Select
                        value={summaryFilter || 'all'}
                        onValueChange={(val) => {
                          const next = val === 'all' ? null : val;
                          setSummaryFilter(next);
                          setCurrentPage(1);
                          fetchData(1, activeSearch, next);
                        }}
                      >
                        <SelectTrigger className="bg-white" data-testid="fines-status-filter">
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all" data-testid="status-opt-all">All statuses</SelectItem>
                          <SelectItem value="paid" data-testid="status-opt-paid">Paid</SelectItem>
                          <SelectItem value="unpaid" data-testid="status-opt-unpaid">Unpaid</SelectItem>
                          <SelectItem value="recovered_from_salary" data-testid="status-opt-recovered">Recovered</SelectItem>
                          <SelectItem value="rider_pay" data-testid="status-opt-rider-pay">Rider Pay</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setFilters({ fromDate: '', toDate: '', lessee: '', rider: '', bike: '' });
                        setSummaryFilter(null);
                        setCurrentPage(1);
                        fetchData(1, activeSearch, null);
                      }}
                      data-testid="clear-fines-filters-btn"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Mobile/Tablet Card View */}
              {(isMobile || isTablet) ? (
                <div className="space-y-3">
                  {sortedFines.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                      <FileText className="w-12 h-12 mb-3 text-slate-300" />
                      <p className="text-sm">No fines recorded</p>
                    </div>
                  ) : (
                    sortedFines.map((fine) => {
                      const fineAmt = Number(fine.amount || fine.fine_amount || 0);
                      const adminAmt = Number(fine.admin_fees || fine.admin_fee || 0);
                      const vatAmt = Math.round(adminAmt * 0.05 * 100) / 100;
                      const total = fineAmt + adminAmt + vatAmt;
                      return (
                        <Card 
                          key={fine.id} 
                          className={`overflow-hidden active:scale-[0.99] transition-transform cursor-pointer ${selectedFines.includes(fine.id) ? 'ring-2 ring-blue-400' : ''}`}
                          onClick={() => handleViewDetails(fine)}
                          data-testid={`fine-card-${fine.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                {/* Header - Ticket No & Date */}
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <Badge className="bg-red-100 text-red-800 font-mono text-xs">
                                    {fine.fine_number || 'N/A'}
                                  </Badge>
                                  <span className="text-sm text-slate-500">
                                    {formatDateDisplay(fine.fine_date || fine.date_of_fine) || '-'}
                                  </span>
                                  <Badge 
                                    className={`text-xs cursor-pointer ${
                                      fine.status === 'paid' ? 'bg-green-100 text-green-800' : 
                                      fine.status === 'unpaid' ? 'bg-red-100 text-red-800' : 
                                      fine.status === 'recovered_from_salary' ? 'bg-blue-100 text-blue-800' :
                                      'bg-yellow-100 text-yellow-800'
                                    }`}
                                    onClick={(e) => { e.stopPropagation(); handleStatusChange(fine); }}
                                  >
                                    {fine.status === 'recovered_from_salary' ? 'Recovered' : fine.status || 'pending'}
                                  </Badge>
                                </div>

                                {/* Plate & Amounts */}
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <PlatePair oldPlate={fine.bike_plate_number || fine.motorcycle_plate} goldenPlate={fine.bike_golden_plate_number} />
                                  <span className="text-lg font-bold text-red-600">
                                    <CurrencyDisplay amount={total} />
                                  </span>
                                </div>

                                {/* Details Grid */}
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                  <div>
                                    <span className="text-slate-500 text-xs">Current Rider:</span>
                                    <p className="text-slate-700 truncate">{fine.current_rider_name || '-'}</p>
                                    <span className="text-slate-500 text-xs">Fine by:</span>
                                    <p className="text-slate-700 truncate">{fine.fine_by_rider_name || '-'}</p>
                                  </div>
                                  <div>
                                    <span className="text-slate-500 text-xs">Company:</span>
                                    <p className="text-slate-700 truncate">{fine.lessee_name || '-'}</p>
                                  </div>
                                </div>

                                {/* Reason */}
                                {fine.reason && (
                                  <p className="text-xs text-slate-500 mt-2 truncate">{fine.reason}</p>
                                )}
                              </div>

                              {/* Actions & Checkbox */}
                              <div className="flex flex-col items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={selectedFines.includes(fine.id)}
                                  onChange={(e) => { e.stopPropagation(); toggleFineSelection(fine.id); }}
                                  className="w-4 h-4 rounded border-slate-300"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewDetails(fine); }}>
                                      <Eye className="mr-2 h-4 w-4" />
                                      View Details
                                    </DropdownMenuItem>
                                    {canEdit && (
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenUpdateFine(fine); }}>
                                        <Edit className="mr-2 h-4 w-4" />
                                        Update Fine
                                      </DropdownMenuItem>
                                    )}
                                    {canEdit && (
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenEditFine(fine); }} data-testid={`edit-fine-${fine.id}`}>
                                        <Edit className="mr-2 h-4 w-4 text-indigo-600" />
                                        Edit Fine
                                      </DropdownMenuItem>
                                    )}
                                    {canEdit && (
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenDocsUpload(fine); }} data-testid={`add-docs-${fine.id}`}>
                                        <Upload className="mr-2 h-4 w-4 text-blue-600" />
                                        Add Documents
                                      </DropdownMenuItem>
                                    )}
                                    {canEdit && (
                                      <DropdownMenuItem
                                        onClick={(e) => { e.stopPropagation(); setReverseFineDialog({ open: true, fine }); }}
                                        className="text-amber-600"
                                        data-testid={`reverse-fine-${fine.id}`}
                                      >
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Reverse Fine
                                      </DropdownMenuItem>
                                    )}
                                    {canDelete && (
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(fine.id); }} className="text-red-600">
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Delete Fine
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              ) : (
                /* Desktop Table View */
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow className="bg-slate-50 border-b border-slate-200">
                      <TableHead className="w-8 px-2">
                        <input
                          type="checkbox"
                          checked={sortedFines.length > 0 && selectedFines.length === sortedFines.length}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </TableHead>
                      <TableHead className="font-semibold text-slate-700 text-xs px-2 whitespace-nowrap w-[160px] text-center">TICKET / PLATE</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-xs px-2 whitespace-nowrap w-[110px] text-center">
                        <div className="flex items-center justify-center gap-1">
                          DATE / TIME
                          <SortButton onSort={(field, dir) => setSortConfig({field, direction: dir})} currentSort={sortConfig} field="date" />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-slate-700 text-xs px-2 whitespace-nowrap text-center">RIDER</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-xs px-2 whitespace-nowrap w-[130px] text-center">LESSEE</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-xs px-2 whitespace-nowrap text-center w-[130px]">AMOUNT</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-xs px-2 w-[110px] text-center">RECOVERY STATUS</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-[11px] px-2 leading-tight w-[120px] whitespace-normal text-center">
                        AUTHORITY PAYMENT STATUS
                      </TableHead>
                      <TableHead className="font-semibold text-slate-700 text-[11px] px-2 text-center leading-tight w-[70px]">
                        <div>TICKET<br/>PHOTO</div>
                      </TableHead>
                      <TableHead className="font-semibold text-slate-700 text-[11px] px-2 text-center leading-tight w-[90px]">
                        <div>ZOHO<br/>INV NO.</div>
                      </TableHead>
                      <TableHead className="font-semibold text-slate-700 text-xs px-2 text-center w-12" title="Actions">
                        <MoreVertical className="w-4 h-4 mx-auto text-slate-500" />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedFines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-slate-500 py-12">
                          <div className="space-y-2">
                            <Search className="w-8 h-8 mx-auto text-slate-400" />
                            <div>No fines recorded. Add your first fine record to get started.</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedFines.map((fine) => {
                        const fineAmt = Number(fine.amount || fine.fine_amount || 0);
                        const adminAmt = Number(fine.admin_fees || fine.admin_fee || 0);
                        const vatAmt = Math.round(adminAmt * 0.05 * 100) / 100;
                        const total = fineAmt + adminAmt + vatAmt;
                        const authPaid = (fine.authority_payment_status || 'Not Paid') === 'Paid';
                        return (
                          <TableRow 
                            key={fine.id}
                            className={`cursor-pointer hover:bg-red-50/50 transition-colors border-b border-slate-100 ${selectedFines.includes(fine.id) ? 'bg-blue-50' : ''}`}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()} className="py-2 px-2">
                              <input
                                type="checkbox"
                                checked={selectedFines.includes(fine.id)}
                                onChange={() => toggleFineSelection(fine.id)}
                                className="w-4 h-4 rounded border-slate-300"
                              />
                            </TableCell>
                            <TableCell onClick={() => handleViewDetails(fine)} className="text-xs py-2 px-2 max-w-[170px]">
                              <div className="leading-tight space-y-1">
                                <PlatePair oldPlate={fine.bike_plate_number || fine.motorcycle_plate} goldenPlate={fine.bike_golden_plate_number} compact />
                                <div className="text-blue-600 whitespace-nowrap font-medium">{fine.fine_number || '-'}</div>
                                {(fine.reason || fine.fine_type) && (
                                  <div className="text-[11px] text-slate-500 truncate" title={fine.reason || fine.fine_type}>
                                    {fine.reason || fine.fine_type}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell onClick={() => handleViewDetails(fine)} className="text-xs py-2 px-2 whitespace-nowrap">
                              <div className="leading-tight">
                                <div className="text-slate-800">{formatDateDisplay(fine.fine_date || fine.date_of_fine) || '-'}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">{fine.fine_time || '-'}</div>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-slate-600 py-2 px-2 max-w-[140px]">
                              <div className="leading-tight">
                                <div className="truncate" title={fine.current_rider_name || '-'}>
                                  <span className="text-slate-400">Current: </span>{fine.current_rider_name || '-'}
                                </div>
                                <div className="truncate text-slate-500" title={fine.fine_by_rider_name || '-'}>
                                  <span className="text-slate-400">Fine by: </span>{fine.fine_by_rider_name || '-'}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-slate-600 py-2 px-2 whitespace-nowrap max-w-[100px] truncate">{fine.lessee_name || '-'}</TableCell>
                            <TableCell className="text-xs py-2 px-2 whitespace-nowrap tabular-nums">
                              <div className="leading-tight flex flex-col items-end gap-0.5">
                                <div className="text-[11px] text-slate-500 inline-flex items-baseline gap-1.5" title="F = Fine, A = Admin fee, V = 5% VAT on Admin">
                                  <span className="inline-flex items-baseline">
                                    <span className="text-slate-400 mr-0.5">F:</span>
                                    <CurrencyDisplay amount={fineAmt} />
                                  </span>
                                  <span className="text-slate-300">·</span>
                                  <span className="inline-flex items-baseline">
                                    <span className="text-slate-400 mr-0.5">A:</span>
                                    <CurrencyDisplay amount={adminAmt} />
                                  </span>
                                  <span className="text-slate-300">·</span>
                                  <span className="inline-flex items-baseline">
                                    <span className="text-slate-400 mr-0.5">V:</span>
                                    <CurrencyDisplay amount={vatAmt} showDecimals={vatAmt % 1 !== 0} />
                                  </span>
                                </div>
                                <div className="font-bold text-green-600 text-sm inline-flex items-baseline">
                                  <CurrencyDisplay amount={total} showDecimals={total % 1 !== 0} />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs py-2 px-2" data-testid={`fine-status-${fine.id}`}>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                fine.status === 'paid' ? 'bg-green-100 text-green-800' :
                                fine.status === 'recovered_from_salary' ? 'bg-blue-100 text-blue-800' :
                                fine.status === 'rider_pay' ? 'bg-indigo-100 text-indigo-800' :
                                'bg-red-100 text-red-800'
                              }`} title={fine.status === 'rider_pay' ? (fine.rider_pay_remark || 'Rider allowed to pay by himself') : ''}>
                                {fine.status === 'recovered_from_salary'
                                  ? 'Recovered'
                                  : fine.status === 'paid'
                                  ? 'Paid'
                                  : fine.status === 'rider_pay'
                                  ? 'Rider Pay'
                                  : 'Unpaid'}
                              </span>
                            </TableCell>
                            {/* Authority (RTA / Municipality) payment status — separate from the
                                Recovery Status which tracks salary/lessee recovery. */}
                            <TableCell className="text-xs py-2 px-2" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-start gap-1">
                                <button
                                  type="button"
                                  onClick={() => setAuthDialog({
                                    open: true, fine, status: fine.authority_payment_status || 'Not Paid',
                                    payment_date: fine.authority_payment_date || '', receipt: null, saving: false,
                                  })}
                                  className={`flex-1 text-left rounded px-2 py-1 text-[11px] leading-tight whitespace-normal break-words hover:ring-1 hover:ring-slate-300 transition ${
                                    authPaid
                                      ? 'bg-green-50 text-green-800 border border-green-200'
                                      : 'bg-orange-50 text-orange-800 border border-orange-200'
                                  }`}
                                  data-testid={`authority-status-${fine.id}`}
                                  title={canEdit ? 'Click to update authority payment' : 'Authority payment status'}
                                >
                                  <div className="font-semibold">
                                    {authPaid ? '✓ Paid' : 'Not Paid'}
                                  </div>
                                  {authPaid && fine.authority_payment_date && (
                                    <div className="text-[10px] text-green-700 mt-0.5">
                                      {formatDateDisplay(fine.authority_payment_date) || fine.authority_payment_date}
                                    </div>
                                  )}
                                </button>
                                {authPaid && fine.authority_payment_receipt_url && (
                                  <button
                                    type="button"
                                    onClick={() => setAuthReceiptViewer({
                                      open: true,
                                      url: fine.authority_payment_receipt_url,
                                      fineNumber: fine.fine_number || fine.ticket_number || '',
                                    })}
                                    className="shrink-0 h-7 w-7 flex items-center justify-center rounded border border-green-200 bg-green-50 hover:bg-green-100 text-green-700 transition"
                                    title="View / download payment receipt"
                                    data-testid={`authority-receipt-view-${fine.id}`}
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="py-2 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                              {(fine.ticket_photo_url || fine.zoho_invoice_url) ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-blue-600 hover:bg-blue-50"
                                  onClick={() => setDocsViewFine(fine)}
                                  title="View documents"
                                  data-testid={`view-docs-${fine.id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              ) : (
                                <span className="text-[11px] text-slate-300">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs py-2 px-2 whitespace-nowrap text-center">
                              {fine.zoho_invoice_no ? (
                                <div className="leading-tight">
                                  <div className="text-slate-800 font-medium">{fine.zoho_invoice_no}</div>
                                  {fine.zoho_invoice_date && (
                                    <div className="text-[10px] text-slate-500">{formatDateDisplay(fine.zoho_invoice_date) || fine.zoho_invoice_date}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[11px] text-slate-300">—</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-44">
                                  <DropdownMenuItem onClick={() => handleViewDetails(fine)} className="cursor-pointer" data-testid="view-details-btn">
                                    <Eye className="mr-2 h-4 w-4" />
                                    View Details
                                  </DropdownMenuItem>
                                  {canEdit && (
                                    <DropdownMenuItem onClick={() => handleOpenUpdateFine(fine)} className="cursor-pointer" data-testid="update-fine-btn">
                                      <Edit className="mr-2 h-4 w-4" />
                                      Update Fine
                                    </DropdownMenuItem>
                                  )}
                                  {canEdit && (
                                    <DropdownMenuItem onClick={() => handleOpenEditFine(fine)} className="cursor-pointer" data-testid={`edit-fine-${fine.id}`}>
                                      <Edit className="mr-2 h-4 w-4 text-indigo-600" />
                                      Edit Fine
                                    </DropdownMenuItem>
                                  )}
                                  {canEdit && (
                                    <DropdownMenuItem onClick={() => handleOpenDocsUpload(fine)} className="cursor-pointer" data-testid={`add-docs-${fine.id}`}>
                                      <Upload className="mr-2 h-4 w-4 text-blue-600" />
                                      Add Documents
                                    </DropdownMenuItem>
                                  )}
                                  {canEdit && (
                                    <DropdownMenuItem onClick={() => { setDiscountFine(fine); setDiscountDialogOpen(true); }} className="cursor-pointer" data-testid={`add-discount-${fine.id}`}>
                                      <TrendingUp className="mr-2 h-4 w-4 text-emerald-600" />
                                      Add Discount
                                    </DropdownMenuItem>
                                  )}
                                  {(fine.proof_file_url || fine.payment_proof_url) && (
                                    <DropdownMenuItem onClick={() => openProofHistory(fine.id)} className="cursor-pointer" data-testid="view-proof-btn">
                                      <FileText className="mr-2 h-4 w-4 text-blue-600" />
                                      View Proof &amp; Audit
                                    </DropdownMenuItem>
                                  )}
                                  {canDelete && (
                                    <DropdownMenuItem 
                                      onClick={() => handleDelete(fine.id)} 
                                      className="cursor-pointer text-red-600 focus:text-red-600"
                                      data-testid="delete-fine-btn"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete Fine
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
                  totalCount={totalCount}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={handlePageChange}
                  loading={loading}
                />
              </div>
              )}
            </CardContent>
          </Card>
        {/* Draft/Pending-Approval tab removed */}
      </div>

      {/* Bulk Ticket Photo Upload — results dialog */}
      <Dialog open={bulkPhotoDialogOpen} onOpenChange={setBulkPhotoDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[85vh] flex flex-col p-0`}>
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-emerald-700" /> Bulk Ticket Photo Upload Results
            </DialogTitle>
          </DialogHeader>
          {bulkPhotoResult && (
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
                  <div className="text-[10px] uppercase text-emerald-700">Matched &amp; uploaded</div>
                  <div className="text-2xl font-bold text-emerald-800" data-testid="bulk-matched">{bulkPhotoResult.matched_count}</div>
                </div>
                <div className="bg-slate-50 border rounded p-3 text-center">
                  <div className="text-[10px] uppercase text-slate-500">Already had photo</div>
                  <div className="text-2xl font-bold text-slate-700">{bulkPhotoResult.skipped_existing_count}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
                  <div className="text-[10px] uppercase text-red-700">Unmatched (no fine)</div>
                  <div className="text-2xl font-bold text-red-800" data-testid="bulk-unmatched">{bulkPhotoResult.unmatched_count}</div>
                </div>
              </div>

              {bulkPhotoResult.unmatched?.length > 0 && (
                <div className="border border-red-200 rounded">
                  <div className="bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-800">
                    Unmatched files ({bulkPhotoResult.unmatched.length}) — no fine found for these filenames. Rename the files to the exact ticket number and re-upload.
                  </div>
                  <div className="max-h-56 overflow-auto p-2 space-y-1">
                    {bulkPhotoResult.unmatched.map((u, i) => (
                      <div key={i} className="text-[11px] font-mono bg-red-50/40 rounded px-2 py-1">
                        {u.filename} <span className="text-red-600">— {u.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bulkPhotoResult.skipped_existing?.length > 0 && (
                <div className="border rounded">
                  <div className="bg-slate-50 px-3 py-2 text-[12px] font-semibold text-slate-700">
                    Skipped — fine already has a ticket photo ({bulkPhotoResult.skipped_existing.length})
                  </div>
                  <div className="max-h-40 overflow-auto p-2 space-y-1">
                    {bulkPhotoResult.skipped_existing.map((s, i) => (
                      <div key={i} className="text-[11px] font-mono bg-white rounded px-2 py-1">
                        {s.filename} <span className="text-slate-500">→ ticket_no {s.ticket_no}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bulkPhotoResult.matched?.length > 0 && (
                <div className="border border-emerald-200 rounded">
                  <div className="bg-emerald-50 px-3 py-2 text-[12px] font-semibold text-emerald-800">
                    Successfully attached ({bulkPhotoResult.matched.length})
                  </div>
                  <div className="max-h-56 overflow-auto p-2 space-y-1">
                    {bulkPhotoResult.matched.map((m, i) => (
                      <div key={i} className="text-[11px] font-mono bg-emerald-50/40 rounded px-2 py-1">
                        {m.filename} <span className="text-emerald-700">→ ticket_no {m.ticket_no}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="px-6 py-3 border-t shrink-0 flex justify-end">
            <Button onClick={() => setBulkPhotoDialogOpen(false)} className="bg-slate-800 hover:bg-slate-900" data-testid="bulk-photo-close">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Ticket Photo Dialog */}
      <Dialog open={ticketPhotoDialogOpen} onOpenChange={(open) => {
        setTicketPhotoDialogOpen(open);
        if (!open) {
          setTicketPhotoFile(null);
          setTicketPhotoFine(null);
        }
      }}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-md'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Upload Ticket Photo
            </DialogTitle>
            <DialogDescription>
              {ticketPhotoFine && (
                <span>
                  Upload a photo of the ticket for fine #{ticketPhotoFine.fine_number || 'N/A'} 
                  ({ticketPhotoFine.bike_plate_number || ticketPhotoFine.motorcycle_plate || 'Unknown plate'})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Current Photo Preview */}
            {ticketPhotoFine?.ticket_photo_url && (
              <div className="space-y-2">
                <Label className="text-slate-600">Current Photo</Label>
                <div className="relative">
                  <img 
                    src={ticketPhotoFine.ticket_photo_url} 
                    alt="Current ticket" 
                    className="w-full h-40 object-contain rounded-lg border border-slate-200 bg-slate-50"
                  />
                  <Badge className="absolute top-2 right-2 bg-blue-100 text-blue-800">
                    Current
                  </Badge>
                </div>
              </div>
            )}

            {/* File Upload Input */}
            <div className="space-y-2">
              <Label>{ticketPhotoFine?.ticket_photo_url ? 'Replace with new photo' : 'Select photo to upload'}</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setTicketPhotoFile(e.target.files[0])}
                className="cursor-pointer"
                data-testid="ticket-photo-input"
              />
              <p className="text-xs text-slate-500">Accepted formats: JPG, PNG, WEBP</p>
            </div>

            {/* New Photo Preview */}
            {ticketPhotoFile && (
              <div className="space-y-2">
                <Label className="text-slate-600">New Photo Preview</Label>
                <div className="relative">
                  <img 
                    src={URL.createObjectURL(ticketPhotoFile)} 
                    alt="New ticket preview" 
                    className="w-full h-40 object-contain rounded-lg border border-green-200 bg-green-50"
                  />
                  <Badge className="absolute top-2 right-2 bg-green-100 text-green-800">
                    New
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">
                  {ticketPhotoFile.name} ({(ticketPhotoFile.size / 1024).toFixed(1)} KB)
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 justify-end pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setTicketPhotoDialogOpen(false);
                  setTicketPhotoFile(null);
                  setTicketPhotoFine(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUploadTicketPhoto}
                disabled={!ticketPhotoFile || uploadingPhoto}
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                data-testid="upload-ticket-photo-btn"
              >
                {uploadingPhoto ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Photo
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Fine Dialog — Admin / Super Admin only. Lets the user
          correct the core fine details (date, time, amount, ticket
          reference) without touching payment status. */}
      <Dialog open={!!editFine} onOpenChange={(v) => !v && setEditFine(null)}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-lg'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-4 h-4 text-indigo-600" /> Edit Fine Details
            </DialogTitle>
            <DialogDescription>
              Restricted to Admin / Super Admin. Editing here corrects the RTA
              record itself (date / amount / reference no.). Payment status is
              managed separately via <em>Update Fine</em>. To upload proof or
              invoice, use <em>Add Documents</em>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fine Date</Label>
                <Input type="date" value={editForm.fine_date} onChange={(e) => setEditForm({ ...editForm, fine_date: e.target.value })} className="h-9" data-testid="edit-fine-date" />
              </div>
              <div>
                <Label className="text-xs">Fine Time</Label>
                <Input type="time" value={editForm.fine_time} onChange={(e) => setEditForm({ ...editForm, fine_time: e.target.value })} className="h-9" data-testid="edit-fine-time" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Amount (AED)</Label>
              <Input type="number" step="0.01" min="0" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} className="h-9" data-testid="edit-fine-amount" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fine Number</Label>
                <Input value={editForm.fine_number} onChange={(e) => setEditForm({ ...editForm, fine_number: e.target.value })} className="h-9" placeholder="RTA fine no." data-testid="edit-fine-number" />
              </div>
              <div>
                <Label className="text-xs">Payment Reference No.</Label>
                <Input value={editForm.payment_ref_no} onChange={(e) => setEditForm({ ...editForm, payment_ref_no: e.target.value })} className="h-9" placeholder="Bank / gateway ref" data-testid="edit-fine-refno" />
              </div>
            </div>
            {editFine && (
              <div className="text-[11px] text-slate-500 bg-slate-50 border rounded px-2 py-1.5">
                Editing fine <strong>{editFine.fine_number || editFine.id?.slice(0, 8)}</strong> · Rider <strong>{editFine.rider_name || editFine.fine_by_rider_name || '—'}</strong>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFine(null)}>Cancel</Button>
            <Button onClick={submitEditFine} disabled={savingEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edit-fine-save">
              {savingEdit ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Replace Documents dialog — accessible to anyone with
          fine-edit permission. Uploads Ticket Photo and/or Zoho
          Invoice; both files are optional. */}
      <Dialog open={!!docsUploadFine} onOpenChange={(v) => { if (!v) { setDocsUploadFine(null); setDocsTicketPhoto(null); setDocsZohoInvoice(null); } }}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-md'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-blue-600" /> Add Documents
            </DialogTitle>
            <DialogDescription>
              Upload the RTA Ticket Photo and/or Zoho Invoice for fine{' '}
              <strong>{docsUploadFine?.fine_number || docsUploadFine?.id?.slice(0, 8)}</strong>.
              Existing files will be replaced.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Ticket Photo (from Authority portal)</Label>
              {docsUploadFine?.ticket_photo_url && (
                <div className="text-[10px] text-emerald-700 mb-1">✓ Already uploaded — pick a new file to replace</div>
              )}
              <Input type="file" accept="application/pdf,image/*" onChange={(e) => setDocsTicketPhoto(e.target.files?.[0] || null)} className="h-9" data-testid="docs-upload-ticket-photo" />
            </div>
            <div>
              <Label className="text-xs">Zoho Invoice</Label>
              {docsUploadFine?.zoho_invoice_url && (
                <div className="text-[10px] text-emerald-700 mb-1">✓ Already uploaded — pick a new file to replace</div>
              )}
              <Input type="file" accept="application/pdf,image/*" onChange={(e) => setDocsZohoInvoice(e.target.files?.[0] || null)} className="h-9" data-testid="docs-upload-zoho-invoice" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Zoho Invoice No.</Label>
                <Input
                  value={docsZohoInvoiceNo}
                  onChange={(e) => setDocsZohoInvoiceNo(e.target.value)}
                  placeholder="e.g. INV-000123"
                  className="h-9"
                  data-testid="docs-zoho-invoice-no"
                />
              </div>
              <div>
                <Label className="text-xs">Zoho Invoice Date</Label>
                <Input
                  type="date"
                  value={docsZohoInvoiceDate}
                  onChange={(e) => setDocsZohoInvoiceDate(e.target.value)}
                  className="h-9"
                  data-testid="docs-zoho-invoice-date"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDocsUploadFine(null); setDocsTicketPhoto(null); setDocsZohoInvoice(null); setDocsZohoInvoiceNo(''); setDocsZohoInvoiceDate(''); }}>Cancel</Button>
            <Button onClick={submitDocsUpload} disabled={savingDocs} className="bg-blue-600 hover:bg-blue-700" data-testid="docs-upload-save">
              {savingDocs ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Save Documents
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents viewer — shows Ticket Photo + Zoho Invoice side-by-
          side for the selected fine. Uses <img>/<iframe> tags directly
          since the URLs are served under the same origin. */}
      <Dialog open={!!docsViewFine} onOpenChange={(v) => !v && setDocsViewFine(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Fine Documents</DialogTitle>
            <DialogDescription>
              Ticket Photo (authority) and Zoho Invoice for fine{' '}
              <strong>{docsViewFine?.fine_number || docsViewFine?.id?.slice(0, 8)}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border rounded p-2 bg-slate-50">
              <div className="text-xs font-semibold text-slate-700 mb-1">Ticket Photo</div>
              {docsViewFine?.ticket_photo_url ? (
                /\.pdf(\?|$)/i.test(docsViewFine.ticket_photo_url)
                  ? <iframe title="ticket" src={docsViewFine.ticket_photo_url} className="w-full h-[60vh] bg-white" />
                  : <img src={docsViewFine.ticket_photo_url} alt="ticket" className="w-full max-h-[60vh] object-contain" />
              ) : <div className="text-xs text-slate-400 text-center py-8">Not uploaded</div>}
            </div>
            <div className="border rounded p-2 bg-slate-50">
              <div className="text-xs font-semibold text-slate-700 mb-1">Zoho Invoice</div>
              {docsViewFine?.zoho_invoice_url ? (
                /\.pdf(\?|$)/i.test(docsViewFine.zoho_invoice_url)
                  ? <iframe title="zoho" src={docsViewFine.zoho_invoice_url} className="w-full h-[60vh] bg-white" />
                  : <img src={docsViewFine.zoho_invoice_url} alt="zoho" className="w-full max-h-[60vh] object-contain" />
              ) : <div className="text-xs text-slate-400 text-center py-8">Not uploaded</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocsViewFine(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Fine Dialog */}
      <Dialog open={updateFineDialogOpen} onOpenChange={(open) => {
        setUpdateFineDialogOpen(open);
        if (!open) {
          setUpdateFine(null);
          setPaymentProofFile(null);
        }
      }}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Update Fine
            </DialogTitle>
            <DialogDescription>
              Fine #{updateFine?.fine_number || 'N/A'} — {updateFine?.bike_plate_number || updateFine?.motorcycle_plate || 'Unknown plate'}
            </DialogDescription>
          </DialogHeader>
          {updateFine && (
            <div className="space-y-4">
              {/* Fine Details (Read-only) */}
              <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} text-sm`}>
                <div>
                  <Label className="text-slate-500 text-xs">Date</Label>
                  <p className="font-medium">{formatDateDisplay(updateFine.fine_date) || '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Bike Plate</Label>
                  <div className="mt-1"><PlatePair oldPlate={updateFine.bike_plate_number || updateFine.motorcycle_plate} goldenPlate={updateFine.bike_golden_plate_number} /></div>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Current Rider</Label>
                  <p className="font-medium">{updateFine.current_rider_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Fine by</Label>
                  <p className="font-medium">{updateFine.fine_by_rider_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Reason</Label>
                  <p className="font-medium">{updateFine.reason || updateFine.fine_type || '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Total Amount</Label>
                  <p className="font-bold text-green-600">
                    <CurrencyDisplay amount={(updateFine.amount || updateFine.fine_amount || 0) + (updateFine.admin_fees || updateFine.admin_fee || 0)} />
                  </p>
                </div>
              </div>

              <hr className="border-slate-200" />

              {/* Status Update */}
              <div className="space-y-2">
                <Label className="font-semibold">Change Status</Label>
                <select
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  data-testid="update-fine-status-select"
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              {/* Payment Proof Upload - Required when status is "paid" */}
              {updateStatus === 'paid' && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="font-semibold text-blue-800">Proof of Payment {!updateFine.payment_proof_url && <span className="text-red-500">*</span>}</Label>
                  
                  {/* Existing proof preview */}
                  {updateFine.payment_proof_url && (
                    <div className="space-y-1">
                      <p className="text-xs text-blue-600">Existing proof uploaded:</p>
                      <div className="relative group">
                        <img 
                          src={updateFine.payment_proof_url} 
                          alt="Payment proof" 
                          className="w-full max-h-40 object-contain rounded-lg border border-blue-200 bg-white cursor-pointer"
                          onClick={() => window.open(updateFine.payment_proof_url, '_blank')}
                        />
                      </div>
                    </div>
                  )}

                  {/* File input */}
                  <div className="space-y-1">
                    <Label className="text-sm text-blue-700">
                      {updateFine.payment_proof_url ? 'Replace proof file' : 'Upload proof file'}
                    </Label>
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setPaymentProofFile(e.target.files[0])}
                      className="cursor-pointer bg-white"
                      data-testid="payment-proof-input"
                    />
                    <p className="text-xs text-blue-500">Accepted: JPG, PNG, WEBP, PDF</p>
                  </div>

                  {/* New file preview */}
                  {paymentProofFile && (
                    <div className="space-y-1">
                      <p className="text-xs text-green-600">New file selected:</p>
                      {paymentProofFile.type.startsWith('image/') ? (
                        <img 
                          src={URL.createObjectURL(paymentProofFile)} 
                          alt="New proof preview" 
                          className="w-full max-h-40 object-contain rounded-lg border border-green-200 bg-white"
                        />
                      ) : (
                        <div className="flex items-center gap-2 p-2 bg-white rounded border border-green-200">
                          <FileText className="w-5 h-5 text-red-500" />
                          <span className="text-sm">{paymentProofFile.name}</span>
                        </div>
                      )}
                      <p className="text-xs text-slate-500">
                        {paymentProofFile.name} ({(paymentProofFile.size / 1024).toFixed(1)} KB)
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Optional operator note — captured against the fine's
                  audit trail so future viewers see WHY the status was
                  changed (e.g. "Paid at Al Barsha counter by rider"). */}
              <div className="space-y-2">
                <Label htmlFor="update-fine-notes" className="text-sm font-medium text-slate-700">
                  Notes / Description <span className="text-slate-400 text-xs">(optional)</span>
                </Label>
                <Textarea
                  id="update-fine-notes"
                  rows={3}
                  value={updateFine.update_notes || ''}
                  onChange={(e) => setUpdateFine({ ...updateFine, update_notes: e.target.value })}
                  placeholder="Add any context about this payment / status change (e.g. cash receipt no., counter, remarks)…"
                  className="resize-none"
                  data-testid="update-fine-notes"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setUpdateFineDialogOpen(false);
                    setUpdateFine(null);
                    setPaymentProofFile(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitUpdateFine}
                  disabled={updatingFine}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600"
                  data-testid="submit-update-fine-btn"
                >
                  {updatingFine ? 'Updating...' : 'Update Fine'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Mark Paid With Proof of Payment (audit-grade) ===== */}
      <Dialog open={paidProofDialog.open} onOpenChange={(o) => { if (!o && !paidProofSubmitting) closePaidProofDialog(); }}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-xl'} max-h-[90vh] overflow-y-auto`} data-testid="paid-proof-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              {paidProofDialog.existingProofUrl ? 'Replace Proof of Payment' : 'Mark as Paid — Proof of Payment Required'}
            </DialogTitle>
            <DialogDescription>
              {paidProofDialog.contextLabel}
              {paidProofDialog.mode === 'bulk' && ` — ${paidProofDialog.fineIds.length} fine(s) will be updated`}
              {paidProofDialog.mode === 'lessee_month' && ' — all matching unpaid fines in this period will be updated'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Payment Source */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Payment Source <span className="text-red-500">*</span></Label>
              <select
                value={paidProofForm.payment_source}
                onChange={(e) => setPaidProofForm({ ...paidProofForm, payment_source: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                data-testid="paid-proof-source"
              >
                <option value="rider_direct">Paid by Rider Directly (cash / bank transfer)</option>
                <option value="lessee_claim">Claimed to Lessee</option>
                <option value="salary_deduction">Deducted from Rider Salary</option>
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Payment Reference No. <span className="text-red-500">*</span></Label>
                <Input
                  type="text"
                  value={paidProofForm.payment_ref_no}
                  onChange={(e) => setPaidProofForm({ ...paidProofForm, payment_ref_no: e.target.value })}
                  placeholder="e.g. TXN-2026-04-0451 / Receipt #"
                  data-testid="paid-proof-ref"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Date <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={paidProofForm.payment_date}
                    onChange={(e) => setPaidProofForm({ ...paidProofForm, payment_date: e.target.value })}
                    data-testid="paid-proof-date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Time <span className="text-red-500">*</span></Label>
                  <Input
                    type="time"
                    value={paidProofForm.payment_time}
                    onChange={(e) => setPaidProofForm({ ...paidProofForm, payment_time: e.target.value })}
                    data-testid="paid-proof-time"
                  />
                </div>
              </div>
            </div>

            {/* Existing proof preview when replacing */}
            {paidProofDialog.existingProofUrl && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Existing Proof on File</p>
                <a href={paidProofDialog.existingProofUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline break-all" data-testid="paid-proof-existing-link">{paidProofDialog.existingProofUrl}</a>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-amber-800">Reason for replacing existing proof <span className="text-red-500">*</span></Label>
                  <Textarea
                    rows={2}
                    placeholder="Describe why you are replacing the previously uploaded proof (min 5 characters)…"
                    value={paidProofForm.replace_reason}
                    onChange={(e) => setPaidProofForm({ ...paidProofForm, replace_reason: e.target.value })}
                    data-testid="paid-proof-replace-reason"
                  />
                </div>
              </div>
            )}

            {/* File upload */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                {paidProofDialog.existingProofUrl ? 'New Proof File' : 'Proof of Payment'} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="file"
                accept="image/*,.pdf,application/pdf"
                onChange={(e) => setPaidProofForm({ ...paidProofForm, file: e.target.files[0] || null })}
                className="cursor-pointer"
                data-testid="paid-proof-file"
              />
              <p className="text-[11px] text-slate-500">Accepted: JPG, PNG, WEBP, HEIC, PDF — max 10 MB</p>
              {paidProofForm.file && (
                <div className="mt-2 p-2 rounded border border-green-200 bg-green-50 text-xs flex items-center gap-2">
                  {paidProofForm.file.type.startsWith('image/') ? (
                    <Image className="w-4 h-4 text-green-700" />
                  ) : (
                    <FileText className="w-4 h-4 text-red-600" />
                  )}
                  <span className="font-medium truncate">{paidProofForm.file.name}</span>
                  <span className="text-slate-500 ml-auto">{(paidProofForm.file.size / 1024).toFixed(1)} KB</span>
                </div>
              )}
            </div>

            {/* Optional notes */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Notes (optional)</Label>
              <Textarea
                rows={2}
                placeholder="Any additional context for the audit trail…"
                value={paidProofForm.notes}
                onChange={(e) => setPaidProofForm({ ...paidProofForm, notes: e.target.value })}
                data-testid="paid-proof-notes"
              />
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={closePaidProofDialog} disabled={paidProofSubmitting} data-testid="paid-proof-cancel">
                Cancel
              </Button>
              <Button
                onClick={submitPaidProofDialog}
                disabled={paidProofSubmitting}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                data-testid="paid-proof-submit"
              >
                {paidProofSubmitting ? (
                  <><Loader className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-1.5" /> {paidProofDialog.existingProofUrl ? 'Replace Proof' : 'Mark as Paid'}</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Authority Payment Receipt Viewer (view + download) ===== */}
      <Dialog open={authReceiptViewer.open} onOpenChange={(o) => { if (!o) setAuthReceiptViewer({ open: false, url: '', fineNumber: '' }); }}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-3xl'}`} data-testid="authority-receipt-viewer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Authority Payment Receipt
              {authReceiptViewer.fineNumber && (
                <span className="text-xs font-normal text-slate-500">— Fine # {authReceiptViewer.fineNumber}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {authReceiptViewer.url ? (
              /\.pdf(\?|$)/i.test(authReceiptViewer.url) ? (
                <iframe
                  title="authority-receipt"
                  src={`${process.env.REACT_APP_BACKEND_URL}${authReceiptViewer.url.startsWith('/') ? authReceiptViewer.url : '/' + authReceiptViewer.url}`}
                  className="w-full h-[70vh] bg-white border-0"
                />
              ) : (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${authReceiptViewer.url.startsWith('/') ? authReceiptViewer.url : '/' + authReceiptViewer.url}`}
                  alt="Authority payment receipt"
                  className="w-full max-h-[70vh] object-contain mx-auto"
                />
              )
            ) : (
              <p className="text-center text-slate-500 py-6">No receipt attached.</p>
            )}
          </div>
          <DialogFooter>
            {authReceiptViewer.url && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${authReceiptViewer.url.startsWith('/') ? authReceiptViewer.url : '/' + authReceiptViewer.url}`}
                download
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700 transition"
                data-testid="authority-receipt-download"
              >
                <Download className="w-4 h-4" /> Download
              </a>
            )}
            <Button variant="outline" onClick={() => setAuthReceiptViewer({ open: false, url: '', fineNumber: '' })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Authority Payment Status Editor ===== */}
      <Dialog open={authDialog.open} onOpenChange={(o) => { if (!o) setAuthDialog((d) => ({ ...d, open: false })); }}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-md'}`} data-testid="authority-payment-dialog">
          <DialogHeader>
            <DialogTitle>Authority Payment Status</DialogTitle>
            <DialogDescription>
              Track the payment to RTA / Municipality. Recording this here is
              independent of the recovery status from the rider or lessee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-xs text-slate-500 border rounded p-2 bg-slate-50 space-y-0.5">
              <div><span className="text-slate-400">Fine #</span> <span className="font-medium text-slate-800">{authDialog.fine?.fine_number || '-'}</span></div>
              <div><span className="text-slate-400">Plate</span> <span className="font-medium text-slate-800">{authDialog.fine?.bike_plate_number || '-'}</span></div>
              <div><span className="text-slate-400">Total</span> <span className="font-medium text-slate-800">AED {(() => {
                const a = Number(authDialog.fine?.amount || authDialog.fine?.fine_amount || 0);
                const b = Number(authDialog.fine?.admin_fees || authDialog.fine?.admin_fee || 0);
                return (a + b + b * 0.05).toFixed(2);
              })()}</span></div>
            </div>
            <div className="space-y-1.5">
              <Label>Status *</Label>
              <Select value={authDialog.status} onValueChange={(v) => setAuthDialog((d) => ({ ...d, status: v }))}>
                <SelectTrigger data-testid="authority-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Not Paid">Not Paid</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {authDialog.status === 'Paid' && (
              <>
                <div className="space-y-1.5">
                  <Label>Date of Payment *</Label>
                  <Input
                    type="date"
                    value={authDialog.payment_date}
                    onChange={(e) => setAuthDialog((d) => ({ ...d, payment_date: e.target.value }))}
                    data-testid="authority-payment-date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Payment Receipt {authDialog.fine?.authority_payment_receipt_url ? '(replace)' : '*'}</Label>
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setAuthDialog((d) => ({ ...d, receipt: e.target.files?.[0] || null }))}
                    data-testid="authority-payment-receipt"
                  />
                  {authDialog.fine?.authority_payment_receipt_url && !authDialog.receipt && (
                    <p className="text-[11px] text-emerald-600">
                      ✓ Existing receipt on file — upload only if you want to replace it.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuthDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button
              onClick={async () => {
                if (authDialog.status === 'Paid') {
                  if (!authDialog.payment_date) { toast.error('Payment date is required'); return; }
                  if (!authDialog.fine?.authority_payment_receipt_url && !authDialog.receipt) {
                    toast.error('Payment receipt is required the first time you mark this as Paid'); return;
                  }
                }
                setAuthDialog((d) => ({ ...d, saving: true }));
                try {
                  const fd = new FormData();
                  fd.append('status', authDialog.status);
                  if (authDialog.payment_date) fd.append('payment_date', authDialog.payment_date);
                  if (authDialog.receipt) fd.append('receipt', authDialog.receipt);
                  await axios.post(
                    `${API}/fines/${authDialog.fine.id}/authority-payment`,
                    fd,
                    { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } },
                  );
                  toast.success('Authority payment status updated');
                  setAuthDialog({ open: false, fine: null, status: 'Not Paid', payment_date: '', receipt: null, saving: false });
                  fetchData();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to update authority payment');
                  setAuthDialog((d) => ({ ...d, saving: false }));
                }
              }}
              disabled={authDialog.saving}
              data-testid="authority-payment-save"
            >
              {authDialog.saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Proof of Payment History Viewer ===== */}
      <Dialog open={proofHistoryDialog.open} onOpenChange={(o) => { if (!o) setProofHistoryDialog({ open: false, fineId: '', data: null, loading: false }); }}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[85vh] overflow-y-auto`} data-testid="proof-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Proof of Payment — Audit Trail
            </DialogTitle>
            <DialogDescription>Fine ID: {proofHistoryDialog.fineId?.slice(0, 8)}…</DialogDescription>
          </DialogHeader>
          {proofHistoryDialog.loading ? (
            <div className="flex items-center justify-center py-6"><Loader className="w-5 h-5 animate-spin" /></div>
          ) : proofHistoryDialog.data && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-green-200 bg-green-50 p-3">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Source:</span> <span className="font-medium">{proofHistoryDialog.data.payment_source || '—'}</span></div>
                  <div><span className="text-slate-500">Ref #:</span> <span className="font-medium">{proofHistoryDialog.data.payment_ref_no || '—'}</span></div>
                  <div><span className="text-slate-500">Date:</span> <span className="font-medium">{proofHistoryDialog.data.payment_date || '—'}</span></div>
                  <div><span className="text-slate-500">Time:</span> <span className="font-medium">{proofHistoryDialog.data.payment_time || '—'}</span></div>
                </div>
                {proofHistoryDialog.data.current_proof_file_url && (
                  <div className="mt-2">
                    <a href={proofHistoryDialog.data.current_proof_file_url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline break-all">View current proof file →</a>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">History ({(proofHistoryDialog.data.history || []).length})</p>
                {(proofHistoryDialog.data.history || []).length === 0 ? (
                  <p className="text-xs text-slate-500 py-3 text-center">No history recorded yet.</p>
                ) : (
                  <div className="space-y-2">
                    {proofHistoryDialog.data.history.map((h, i) => (
                      <div key={i} className="rounded border border-slate-200 p-2 text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold capitalize text-slate-700">{(h.action || '').replace('_', ' ')}</span>
                          <span className="text-slate-500">{h.at ? new Date(h.at).toLocaleString() : ''}</span>
                        </div>
                        <div className="text-slate-600">by {h.by || 'system'}</div>
                        {h.action === 'replace_proof' ? (
                          <div className="mt-1 space-y-0.5 text-[11px]">
                            <div className="text-amber-700"><strong>Reason:</strong> {h.replace_reason || '—'}</div>
                            <div>Previous: <a href={h.previous_proof_file_url} target="_blank" rel="noreferrer" className="text-blue-700 underline break-all">{h.previous_proof_file_url || '—'}</a></div>
                            <div>New: <a href={h.new_proof_file_url} target="_blank" rel="noreferrer" className="text-blue-700 underline break-all">{h.new_proof_file_url || '—'}</a></div>
                          </div>
                        ) : (
                          <div className="mt-1 text-[11px] text-slate-600">
                            <div>Ref #{h.payment_ref_no} — {h.payment_source} — {h.payment_date} {h.payment_time}</div>
                            {h.proof_file_url && (
                              <a href={h.proof_file_url} target="_blank" rel="noreferrer" className="text-blue-700 underline break-all">View file →</a>
                            )}
                            {h.notes && <div className="mt-0.5 text-slate-500"><em>Note: {h.notes}</em></div>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>



      {/* Fine Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-3xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>Fine Details</DialogTitle>
            <DialogDescription>{selectedFine?.bike_plate_number} - {formatDateDisplay(selectedFine?.fine_date)}</DialogDescription>
          </DialogHeader>
          {selectedFine && (
            <div className="space-y-4">
              {/* Fine/Ticket Number - Prominently displayed */}
              {selectedFine.fine_number && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Label className="text-blue-700 font-semibold">Fine / Ticket Number</Label>
                    <Badge className="bg-blue-100 text-blue-800">{selectedFine.fine_number}</Badge>
                  </div>
                </div>
              )}

              <Card>
                <CardHeader><CardTitle className="text-lg">Fine Information</CardTitle></CardHeader>
                <CardContent>
                  <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    <div>
                      <Label className="text-slate-600">Date</Label>
                      <p className="font-medium">{formatDateDisplay(selectedFine.fine_date || selectedFine.date_of_fine) || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Time</Label>
                      <p className="font-medium">{selectedFine.fine_time || '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-slate-600">Reason</Label>
                      <p className="font-medium">{selectedFine.reason || selectedFine.fine_type || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Status</Label>
                      <Badge className={selectedFine.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {selectedFine.status}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-slate-600">City</Label>
                      <p className="font-medium">{selectedFine.city || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Source</Label>
                      <p className="font-medium">{selectedFine.source || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg">Vehicle & Rider</CardTitle></CardHeader>
                <CardContent>
                  <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    <div>
                      <Label className="text-slate-600">Bike Plate</Label>
                      <div className="mt-1"><PlatePair oldPlate={selectedFine.bike_plate_number || selectedFine.motorcycle_plate} goldenPlate={selectedFine.bike_golden_plate_number} /></div>
                    </div>
                    <div>
                      <Label className="text-slate-600">Current Rider</Label>
                      <p className="font-medium">{selectedFine.current_rider_name || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Fine by</Label>
                      <p className="font-medium">{selectedFine.fine_by_rider_name || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Emirates ID</Label>
                      <p className="font-mono">{selectedFine.rider_emirates_id || riders.find(r => r.id === selectedFine.rider_id)?.emirates_id || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Passport No.</Label>
                      <p className="font-mono">{selectedFine.rider_passport || riders.find(r => r.id === selectedFine.rider_id)?.passport_number || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Mobile (UAE)</Label>
                      <p>{riders.find(r => r.id === selectedFine.rider_id)?.mobile_uae || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Email</Label>
                      <p className="text-blue-600">{riders.find(r => r.id === selectedFine.rider_id)?.email || '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-slate-600">Lessee Company</Label>
                      <p className="font-medium">{selectedFine.lessee_name || 'N/A'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg">Payment Details</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Fine Amount</Label>
                      <p className="font-medium"><CurrencyDisplay amount={selectedFine.amount} /></p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Admin Fees</Label>
                      <p className="font-medium"><CurrencyDisplay amount={selectedFine.admin_fees} /></p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Total</Label>
                      <p className="font-bold text-lg text-green-600">Dh {((selectedFine.amount || selectedFine.fine_amount || 0) + (selectedFine.admin_fees || selectedFine.admin_fee || 0)).toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Proof Section */}
              {selectedFine.payment_proof_url && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      Payment Proof
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="relative group">
                        {selectedFine.payment_proof_url.startsWith('data:image') || selectedFine.payment_proof_url.match(/\.(jpg|jpeg|png|webp)/i) ? (
                          <img 
                            src={selectedFine.payment_proof_url} 
                            alt="Payment proof" 
                            className="w-full max-h-64 object-contain rounded-lg border border-slate-200 bg-slate-50 cursor-pointer"
                            onClick={() => window.open(selectedFine.payment_proof_url, '_blank')}
                          />
                        ) : (
                          <div className="flex items-center gap-3 p-4 border rounded-lg bg-slate-50">
                            <FileText className="w-8 h-8 text-red-500" />
                            <span className="text-sm font-medium">Payment proof document</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadTicketPhoto(selectedFine.payment_proof_url, `payment_proof_${selectedFine.fine_number || selectedFine.id}`)}
                          className="gap-1"
                          data-testid="download-payment-proof-btn"
                        >
                          <Download className="w-4 h-4" />
                          Download Proof
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ticket Photo Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Image className="w-5 h-5 text-blue-600" />
                    Ticket Photo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedFine.ticket_photo_url ? (
                    <div className="space-y-3">
                      <div className="relative group">
                        <img 
                          src={selectedFine.ticket_photo_url} 
                          alt="Ticket" 
                          className="w-full max-h-64 object-contain rounded-lg border border-slate-200 bg-slate-50 cursor-pointer"
                          onClick={() => window.open(selectedFine.ticket_photo_url, '_blank')}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                          <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium">
                            Click to view full size
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadTicketPhoto(selectedFine.ticket_photo_url, selectedFine.fine_number || selectedFine.id)}
                          className="gap-1"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setTicketPhotoFine(selectedFine);
                            setTicketPhotoDialogOpen(true);
                            setDetailDialogOpen(false);
                          }}
                          className="gap-1"
                        >
                          <Camera className="w-4 h-4" />
                          Replace Photo
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                      <Image className="w-12 h-12 mb-2" />
                      <p className="text-sm">No ticket photo uploaded</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3 gap-1"
                        onClick={() => {
                          setTicketPhotoFine(selectedFine);
                          setTicketPhotoDialogOpen(true);
                          setDetailDialogOpen(false);
                        }}
                      >
                        <Camera className="w-4 h-4" />
                        Upload Photo
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          <div className="flex justify-end mt-4">
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reverse Fine Dialog */}
      <Dialog open={reverseFineDialog.open} onOpenChange={(open) => { if (!open) { setReverseFineDialog({ open: false, fine: null }); setReverseReason(''); setReverseCorrectPlate(''); setReverseProofFile(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2">
              <RefreshCw className="w-5 h-5" /> Reverse Fine
            </DialogTitle>
            <DialogDescription>
              This will mark the fine as <b>reversed</b>, optionally create a corrected fine against the right bike, and (if already recovered) queue a refund credit on the rider's next Post Payroll Adjustment.
            </DialogDescription>
          </DialogHeader>
          {reverseFineDialog.fine && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 rounded p-3 space-y-1">
                <p><span className="text-slate-500">Fine No:</span> <b className="font-mono">{reverseFineDialog.fine.fine_number || '-'}</b></p>
                <p><span className="text-slate-500">Currently on bike:</span> <b>{reverseFineDialog.fine.bike_plate_number || '-'}</b></p>
                <p><span className="text-slate-500">Rider:</span> {reverseFineDialog.fine.rider_name || '-'}</p>
                <p><span className="text-slate-500">Amount:</span> AED {Number((reverseFineDialog.fine.amount || 0) + (reverseFineDialog.fine.admin_fees || 0)).toFixed(2)}</p>
                <p><span className="text-slate-500">Status:</span> <Badge variant="outline">{reverseFineDialog.fine.status || 'pending'}</Badge></p>
              </div>
              <div>
                <Label>Reason for reversal <span className="text-red-500">*</span></Label>
                <Textarea
                  value={reverseReason}
                  onChange={(e) => setReverseReason(e.target.value)}
                  rows={3} placeholder="e.g., RTA portal shows ticket 9012267667 is against plate DXB 2-45075, not DXB 2-75618."
                  data-testid="reverse-fine-reason"
                />
              </div>
              <div>
                <Label>Correct bike plate (optional)</Label>
                <Input
                  value={reverseCorrectPlate}
                  onChange={(e) => setReverseCorrectPlate(e.target.value)}
                  placeholder="DXB 2-45075"
                  data-testid="reverse-fine-correct-plate"
                />
                <p className="text-xs text-slate-500 mt-1">If provided, a new fine will be auto-created against this bike's current rider/lessee.</p>
              </div>
              <div>
                <Label>Proof of correction (optional)</Label>
                <Input
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  onChange={(e) => setReverseProofFile(e.target.files?.[0] || null)}
                  data-testid="reverse-fine-proof"
                />
                <p className="text-xs text-slate-500 mt-1">PDF or image, max 10 MB.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReverseFineDialog({ open: false, fine: null }); setReverseReason(''); setReverseCorrectPlate(''); setReverseProofFile(null); }}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              disabled={reverseSubmitting || !reverseFineDialog.fine || reverseReason.trim().length < 5}
              onClick={async () => {
                if (reverseReason.trim().length < 5) { toast.error('Please provide a reason (min 5 characters)'); return; }
                setReverseSubmitting(true);
                try {
                  const fd = new FormData();
                  fd.append('reason', reverseReason.trim());
                  if (reverseCorrectPlate.trim()) fd.append('correct_bike_plate', reverseCorrectPlate.trim());
                  if (reverseProofFile) fd.append('proof_file', reverseProofFile);
                  const res = await axios.post(
                    `${API}/fines/${reverseFineDialog.fine.id}/reverse`,
                    fd,
                    { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                  );
                  toast.success(res.data?.message || 'Fine reversed');
                  setReverseFineDialog({ open: false, fine: null });
                  setReverseReason(''); setReverseCorrectPlate(''); setReverseProofFile(null);
                  fetchData();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Failed to reverse fine');
                } finally {
                  setReverseSubmitting(false);
                }
              }}
              data-testid="reverse-fine-confirm"
            >
              {reverseSubmitting ? 'Reversing...' : 'Reverse Fine'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddFineDiscountDialog
        open={discountDialogOpen}
        onOpenChange={(o) => { setDiscountDialogOpen(o); if (!o) setDiscountFine(null); }}
        fine={discountFine}
        onSaved={() => fetchData()}
      />

    </div>
  );
};

export default Fines;
